import { KirjanpidonUlkopuolisetKulut } from '@lemon/core-firebase/model/kayttaja'
import { Korko } from '@lemon/core-firebase/service/vahvistettu-korko.service'
import { Timestamp } from '@lemon/core/model/common'
import { CodeCheckService } from '@lemon/core/service/code-check.service'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'
import { Asiakas, Tilikausi } from '../../../../_jaettu-lemonator/model/asiakas'
import { KirjanpitajanNimitiedot } from '../../../../_jaettu-lemonator/model/kirjanpitaja'
import { PaatilikartanProfiili, VeroilmoituksenAvainluvut, VeroilmoituksenValueConstants, VeroilmoitusDraft, VeroilmoitusLomakeTunniste } from '../../../../_jaettu-lemonator/model/kirjanpito'
import { AsiakasJaettuService } from '../../asiakas-jaettu.service'
import { IlmoitusRivi } from '../../base-ilmoitin-export.service'
import { RepeatingMetadata, VeroilmoitusBaseSpecialiazedCalculationService } from '../base-specialized-calculation.service'
import {
  OsingotLocatingCalculationService,
  VeroilmoituksenKaikkiTilisummat,
  VeroilmoituksenLaskija,
  VeroilmoituksenPaivityksenTulos,
  VeroilmoitusValidationError,
} from '../veroilmoitus-laskenta.service'
import { VeroilmoitusSummatService } from '../veroilmoitus-summat.service'

/**
 * 701 V/P*   Osakkeenomistajan nimi    AN70
 * 703 V/P*   Henkilö- tai Y-tunnus     YTUNNUS || HETU
 * 570 V/P*   Osakkeiden lukumäärä      +N15
 * 706 *      Vuokrat                   R13,2
 * 708 *      Korot                     R13,2
 * 707 *      Luovutukset               R13,2
 * 709 *      Muut                      R13,2
 * 864 V/P*   Osakaslainat              R13,2
 */
export interface VeroilmoituksenOsakas {
  /** Osakkeenomistajan nimi */
  '701': string
  /** Henkilö- tai Y-tunnus */
  '703': string
  /** Osakkeiden lukumäärä */
  '570': number
  /** Vuokrat */
  '706': number
  /** Korot */
  '708': number
  /** Luovutukset */
  '707': number
  /** Muut */
  '709': number
  /** Osakaslainat */
  '864': number
}

export interface VeroilmoituksenOsakkaanKaytossaOllutHuoneisto {
  /** Osakkaan henkilötunnus */
  '722': string
  /** Kiinteistön, kiinteistöyhtiön tai asunto-osakeyhtiön nimi */
  '724': string
  /** Käyttöaika asuntona (ppkkvvvv - ppkkvvvv) */
  '729': string
  /** Nettovarallisuuslaskelmassa käytetty arvo */
  '730': number
}

/**
 * NOTE!! OBS!! NB!!
 * If you change this, namely 'osingot',
 * the key is used in lemonator-web/functions/src/tilinpaatos/first-user-signed-yhtiokokous.handler.ts
 * When initializing these values based on hallitus decision after the yhtiökokous pöytäkirja is signed.
 */

export enum RepeatingPartIdentifiers {
  HUONEISTOT = 'huoneistot',
  OSINGOT = 'osingot',
  VAPAASTA_PAAOMASTA_JAETUT = 'vapaasta',
  OSAKKAAT = 'osakkaat',
}

export class VeroilmoitusLaskenta2025OyService extends VeroilmoitusBaseSpecialiazedCalculationService implements VeroilmoituksenLaskija, OsingotLocatingCalculationService {
  constructor(
    private _dateService: DateService,
    protected _currencyService: CurrencyService,
    protected _codeCheckService: CodeCheckService,
    private _asiakasJaettuService: AsiakasJaettuService,
  ) {
    super(_currencyService, _codeCheckService)
  }

  private _veroilmoituksenOsinkojenKentat: Set<string> = new Set(['537', '538', '522'])
  parsiOsinkojenMetadata(arvot: IlmoitusRivi[]): RepeatingMetadata[] {
    const indexOfFirst = arvot.findIndex(a => this._veroilmoituksenOsinkojenKentat.has(a.key))
    if (indexOfFirst > -1) {
      return this._parsiMetadata(arvot, RepeatingPartIdentifiers.OSINGOT, indexOfFirst - 1)
    }
    return []
  }

  private _veroilmoituksenVapaastaPaaomastaJaettujenKentat: Set<string> = new Set(['293', '294', '295'])
  parsiVapaastaPaaomastaJaettujenMetadata(arvot: IlmoitusRivi[]): RepeatingMetadata[] {
    const indexOfFirst = arvot.findIndex(a => this._veroilmoituksenVapaastaPaaomastaJaettujenKentat.has(a.key))
    if (indexOfFirst > -1) {
      return this._parsiMetadata(arvot, RepeatingPartIdentifiers.VAPAASTA_PAAOMASTA_JAETUT, indexOfFirst - 1)
    }
    return []
  }

  private _veroilmoituksenOsakkaanKentat: Set<string> = new Set(['701', '703', '570', '706', '708', '707', '709', '864'])
  parsiOsakkaidenMetadata(arvot: IlmoitusRivi[]): RepeatingMetadata[] {
    const indexOf44 = arvot.findIndex(a => a.key === '044')
    if (indexOf44 > -1) {
      if (this._varmistaEttaEnsimmainenKenttaOnYksiSallituista(arvot, indexOf44 + 1, this._veroilmoituksenOsakkaanKentat)) {
        return this._parsiMetadata(arvot, RepeatingPartIdentifiers.OSAKKAAT, indexOf44 + 1)
      }
    }
    return []
  }

  parsiOsakasHuoneistojenMetadata(arvot: IlmoitusRivi[]): RepeatingMetadata[] {
    const indexOfFirst722 = arvot.findIndex(a => a.key === '722')
    if (indexOfFirst722 > 0) {
      return this._parsiMetadata(arvot, RepeatingPartIdentifiers.HUONEISTOT, indexOfFirst722 - 1)
    }
    return []
  }

  groupIlmoitusrivitByOsakkaat(arvot: IlmoitusRivi[]): IlmoitusRivi[][] {
    const repeatingMetadata = this.parsiOsakkaidenMetadata(arvot)
    return repeatingMetadata.map(repeat => super._getRivitForRepeatingPart(repeat, arvot))
  }
  groupIlmoitusrivitByOsakkaidenHuoneistot(arvot: IlmoitusRivi[]): IlmoitusRivi[][] {
    const repeatingMetadata = this.parsiOsakasHuoneistojenMetadata(arvot)
    return repeatingMetadata.map(repeat => super._getRivitForRepeatingPart(repeat, arvot))
  }
  groupIlmoitusrivitByOsingot(arvot: IlmoitusRivi[]): IlmoitusRivi[][] {
    const repeatingMetadata = this.parsiOsinkojenMetadata(arvot)
    return repeatingMetadata.map(repeat => super._getRivitForRepeatingPart(repeat, arvot))
  }
  groupIlmoitusrivitByVapaastaPaaomastaJaettujen(arvot: IlmoitusRivi[]): IlmoitusRivi[][] {
    const repeatingMetadata = this.parsiVapaastaPaaomastaJaettujenMetadata(arvot)
    return repeatingMetadata.map(repeat => super._getRivitForRepeatingPart(repeat, arvot))
  }

  public muodostaVeroilmoituksenLopullisetArvot(
    tietokannasta: VeroilmoitusDraft,
    eiTallennetut: VeroilmoituksenAvainluvut,
    tilienSummat: VeroilmoituksenKaikkiTilisummat,
    summatService: VeroilmoitusSummatService,
    asiakas: Asiakas,
    kirjanpitaja: KirjanpitajanNimitiedot,
    tilikausi: Tilikausi,
    kirjanpidonUlkopuolisetKulut: KirjanpidonUlkopuolisetKulut,
    muodostettu: Timestamp, // Tallennusaika
    peruskorko: Korko,
    tilikarttaProfiili: PaatilikartanProfiili['avain'],
  ): VeroilmoituksenPaivityksenTulos {
    const tilikaudenSummat = tilienSummat.nykyinen?.tilikaudenSummat ?? {}
    const edellisenTilikaudenSummat = tilienSummat.edellinen?.tilikaudenSummat ?? {}

    // The list containing the produced values
    const arvot: IlmoitusRivi[] = []
    const oletukset: VeroilmoituksenAvainluvut = {}
    const affectedAccounts: Set<string> = new Set()
    const ignoredAccounts: Set<string> = new Set()

    this.tietokannastaLaskennoille = tietokannasta
    this.eiTallennetutLaskennoille = eiTallennetut
    this.oletuksetLaskennoille = oletukset

    if (!kirjanpitaja) {
      throw new Error('Kirjanpitäjä puuttuu')
    }

    const muodostettuAsDate = this._dateService.timestampToLocalDate(muodostettu)
    const vastuukirjanpitaja = this._asiakasJaettuService.annaVastuukirjanpitajaPaivalle(asiakas, muodostettuAsDate)
    const isHolviZen = vastuukirjanpitaja?.kirjanpitajanAvain === 'QgPvtcCjoOdf6Zg7lgMwqLWp2BG2'
    const accountantPhoneNumber = isHolviZen
      ? this._asiakasJaettuService.ZEN_TEAM_NUMBER // Use office number for Holvi Zen customers
      : kirjanpitaja.puhelin

    if (!accountantPhoneNumber) {
      throw new Error('Kirjanpitäjän puhelinnumero puuttuu')
    }

    if (!kirjanpitaja.email || !kirjanpitaja.email.endsWith('@lemontree.fi')) {
      throw new Error('Kirjanpitäjän email puuttuu')
    }

    // Calculate!

    // Specs here: https://lemontree-squad.monday.com/boards/615797797/pulses/860432205

    const formattedDateAndTimeStr = this._dateService.muotoileVeroFormaattiPaivaJaAikaDate(this._dateService.timestampToDate(muodostettu))
    arvot.push({ key: '000', value: 'VSY06B25' }) // File identifier
    arvot.push({ key: '048', value: 'Tilitoimisto Lemon Tree Oy' }) // ID of the software that produced the file
    arvot.push({ key: '014', value: '2599105-8_LT' }) // ID of the software that produced the file (y-tunnus + 2 alphanums)
    arvot.push({ key: '198', value: formattedDateAndTimeStr }) // Software-generated timestamp
    // 111111 TAUSTATIEDOT
    // 111111 TAUSTATIEDOT
    // 111111 TAUSTATIEDOT
    // 111111 TAUSTATIEDOT
    // Asiakastiedot:
    // Haetaan asiakkaan tiedoista Nimi,
    // 010 = Y - tunnus
    arvot.push({ key: '010', value: asiakas.ytunnus })

    // 054 = tilikausi, jolta veroilmoitusta ollaan antamassa.
    const tilikausiFormatted = this._dateService.muotoileVeroFormaattiPaiva(tilikausi.alkaa) + '-' + this._dateService.muotoileVeroFormaattiPaiva(tilikausi.loppuu)
    arvot.push({ key: '054', value: tilikausiFormatted })

    const v169 = this._asetaArvo(
      '169',
      arvot,
      () => '0',
      arvo => arvo === '1',
    )
    const v173 = this._asetaArvo(
      '173',
      arvot,
      () => '0',
      arvo => arvo === '1',
    )
    const v184 = this._asetaArvo(
      '184',
      arvot,
      () => '0',
      arvo => arvo === '1',
    )
    const v182 = this._asetaArvo(
      '182',
      arvot,
      () => '0',
      arvo => arvo === '1',
    )
    const v174 = this._asetaArvo(
      '174',
      arvot,
      () => '0',
      arvo => arvo === '1',
    )
    const v176 = this._asetaArvo(
      '176',
      arvot,
      () => '0',
      arvo => arvo === '1',
    )

    // Lisätietojen antaja:
    // 041 = vastuukirjanpitäjän nimi
    arvot.push({ key: '041', value: kirjanpitaja.etunimi + ' ' + kirjanpitaja.sukunimi })
    // 042 = vastuukirjanpitäjän puhelinnumero
    arvot.push({ key: '042', value: accountantPhoneNumber })
    // 044 = vastuukirjanpitäjän sähköposti
    arvot.push({ key: '044', value: isHolviZen ? 'holvi@lemontree.fi' : kirjanpitaja.email })

    // HUOM!!!!! 044 ja osakkaiden välillä EI SAA OLLA YHTÄÄN ENTRYÄ!!!!
    // HUOM!!!!! 044 ja osakkaiden välillä EI SAA OLLA YHTÄÄN ENTRYÄ!!!!
    // HUOM!!!!! 044 ja osakkaiden välillä EI SAA OLLA YHTÄÄN ENTRYÄ!!!!

    // Osakastiedot
    /**
     * 001 Osatietoryhmien lukumäärä     N8
     *
     * 701 V/P*   Osakkeenomistajan nimi    AN70
     * 703 V/P*   Henkilö- tai Y-tunnus     YTUNNUS || HETU
     * 570 V/P*   Osakkeiden lukumäärä      +N15
     * 706 *      Vuokrat                   R13,2
     * 708 *      Korot                     R13,2
     * 707 *      Luovutukset               R13,2
     * 709 *      Muut                      R13,2
     * 864 V/P*   Osakaslainat              R13,2
     *
     * 009 Osatietoryhmän loppumerkki: juokseva nro N8
     */
    // Get osakkaat defaults
    if (tietokannasta.osakkaat?.length) {
      arvot.push({ key: '001', value: tietokannasta.osakkaat.length + '' })
      let order = 1
      for (const osakas of tietokannasta.osakkaat) {
        const tunniste701 = this.annaTunniste('701', RepeatingPartIdentifiers.OSAKKAAT, order)
        const tunniste703 = this.annaTunniste('703', RepeatingPartIdentifiers.OSAKKAAT, order)
        const tunniste570 = this.annaTunniste('570', RepeatingPartIdentifiers.OSAKKAAT, order)
        const tunniste706 = this.annaTunniste('706', RepeatingPartIdentifiers.OSAKKAAT, order)
        const tunniste708 = this.annaTunniste('708', RepeatingPartIdentifiers.OSAKKAAT, order)
        const tunniste707 = this.annaTunniste('707', RepeatingPartIdentifiers.OSAKKAAT, order)
        const tunniste709 = this.annaTunniste('709', RepeatingPartIdentifiers.OSAKKAAT, order)
        const tunniste864 = this.annaTunniste('864', RepeatingPartIdentifiers.OSAKKAAT, order)

        const v701 = this._annaArvoString(tunniste701, () => osakas.nimi)
        const v703 = this._annaArvoString(tunniste703, () => (osakas.hetuTaiYtunnus ?? '').toUpperCase())
        const v570 = this._annaNumero(tunniste570, () => osakas.osakkeita)

        const v706 = this._annaNumero(tunniste706)
        const v708 = this._annaNumero(tunniste708)
        const v707 = this._annaNumero(tunniste707)
        const v709 = this._annaNumero(tunniste709)
        const v864 = this._annaNumero(tunniste864)

        arvot.push({ key: '701', value: v701 })
        arvot.push({ key: '703', value: v703 })
        arvot.push({ key: '570', value: Math.round(v570) + '' })
        this._setOptionalLuku('706', v706, arvot)
        this._setOptionalLuku('708', v708, arvot)
        this._setOptionalLuku('707', v707, arvot)
        this._setOptionalLuku('709', v709, arvot)
        this._setOptionalLuku('864', v864, arvot)

        arvot.push({ key: '009', value: order + '' })

        order++
      }
    }

    const v185 = this._asetaArvo(
      '185',
      arvot,
      () => VeroilmoituksenValueConstants.FALSE,
      arvo => arvo === VeroilmoituksenValueConstants.TRUE,
    )
    const v571 = this._asetaArvo('571', arvot)

    oletukset['osakkaiden-huoneistot-part'] = VeroilmoituksenValueConstants.FALSE
    const osakkaidenHuoneistotAuki = this._annaArvo('osakkaiden-huoneistot-part', () => VeroilmoituksenValueConstants.FALSE)
    if (osakkaidenHuoneistotAuki === VeroilmoituksenValueConstants.TRUE) {
      const huoneistojenLukumaara = this._annaNumero('huoneistot', () => 0)
      if (huoneistojenLukumaara > 0) {
        arvot.push({ key: '001', value: Math.round(huoneistojenLukumaara) + '' })
        for (let order = 1; order <= huoneistojenLukumaara; order++) {
          const tunniste722 = this.annaTunniste('722', RepeatingPartIdentifiers.HUONEISTOT, order)
          const tunniste724 = this.annaTunniste('724', RepeatingPartIdentifiers.HUONEISTOT, order)
          const tunniste729 = this.annaTunniste('729', RepeatingPartIdentifiers.HUONEISTOT, order)
          const tunniste730 = this.annaTunniste('730', RepeatingPartIdentifiers.HUONEISTOT, order)

          const v722 = this._annaArvo(tunniste722)
          const v724 = this._annaArvo(tunniste724)
          const v729 = this._annaArvo(tunniste729)
          const v730 = this._annaNumero(tunniste730)

          arvot.push({ key: '722', value: (v722 ?? '') + '' })
          arvot.push({ key: '724', value: (v724 ?? '') + '' })
          arvot.push({ key: '729', value: (v729 ?? '') + '' })
          arvot.push({ key: '730', value: this._annaLuku(v730) })

          arvot.push({ key: '009', value: order + '' })
        }
      }
    }

    const onkoOsingotOletuksenaAuki = false
    const onkoOsingotAuki = this._onkoOsaAuki(onkoOsingotOletuksenaAuki, 'jaettavat-osingot-part')
    oletukset['jaettavat-osingot-part'] = onkoOsingotOletuksenaAuki ? VeroilmoituksenValueConstants.TRUE : VeroilmoituksenValueConstants.FALSE
    if (onkoOsingotAuki) {
      const osinkojenLukumaara = this._annaNumero(RepeatingPartIdentifiers.OSINGOT, () => 0)
      if (osinkojenLukumaara > 0) {
        arvot.push({ key: '001', value: Math.round(osinkojenLukumaara) + '' })
        for (let order = 1; order <= osinkojenLukumaara; order++) {
          const tunniste537 = this.annaTunniste('537', RepeatingPartIdentifiers.OSINGOT, order)
          const tunniste538 = this.annaTunniste('538', RepeatingPartIdentifiers.OSINGOT, order)
          const tunniste522 = this.annaTunniste('522', RepeatingPartIdentifiers.OSINGOT, order)

          const v537 = this._annaArvo(tunniste537)
          const v538 = this._annaArvo(tunniste538)
          const v522 = this._annaNumero(tunniste522)

          arvot.push({ key: '537', value: (v537 ?? '') + '' })
          arvot.push({ key: '538', value: (v538 ?? '') + '' })
          arvot.push({ key: '522', value: this._annaLuku(v522) })

          arvot.push({ key: '009', value: order + '' })
        }
      }
      const vapaanOmanPaaomanRahastostaJaetutLukumaara = this._annaNumero('vapaasta', () => 0)
      if (vapaanOmanPaaomanRahastostaJaetutLukumaara > 0) {
        arvot.push({ key: '001', value: Math.round(vapaanOmanPaaomanRahastostaJaetutLukumaara) + '' })
        for (let order = 1; order <= vapaanOmanPaaomanRahastostaJaetutLukumaara; order++) {
          const tunniste293 = this.annaTunniste('293', RepeatingPartIdentifiers.VAPAASTA_PAAOMASTA_JAETUT, order)
          const tunniste294 = this.annaTunniste('294', RepeatingPartIdentifiers.VAPAASTA_PAAOMASTA_JAETUT, order)
          const tunniste295 = this.annaTunniste('295', RepeatingPartIdentifiers.VAPAASTA_PAAOMASTA_JAETUT, order)

          const v293 = this._annaNumero(tunniste293)
          const v294 = this._annaArvo(tunniste294)
          const v295 = this._annaArvo(tunniste295)

          arvot.push({ key: '294', value: (v294 ?? '') + '' })
          arvot.push({ key: '295', value: (v295 ?? '') + '' })
          arvot.push({ key: '293', value: this._annaLuku(v293) })

          arvot.push({ key: '009', value: order + '' })
        }
      }
    }

    const v591 = this._asetaNumero('591', arvot)
    const v592 = this._asetaNumero('592', arvot)
    const v586 = this._asetaArvo(
      '586',
      arvot,
      () => '0',
      arvo => arvo === '1',
    )

    // Tuloverolaskelma

    // Liiketoiminta ja oma käyttö
    const v330 = this._asetaNumero('330', arvot, () => 0 - summatService.laskeDebetMinusKredit('30', tilikaudenSummat, affectedAccounts))
    const v331 = this._asetaNumero('331', arvot, () => 0 - summatService.laskeDebetMinusKredit('36', tilikaudenSummat, affectedAccounts))

    // Toiminnan muut tuotot
    const v332 = this._asetaNumero('332', arvot)

    const v333 = this._asetaNumero('333', arvot, () => 0)

    const v260 = this._asetaNumero('260', arvot, () => 0)

    const v334 = this._asetaNumero('334', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        const t3650to3700 = 0 - summatService.laskeDebetMinusKreditTilialueelta(3650, 3700, tilikaudenSummat, affectedAccounts)
        return t3650to3700
      } else {
        const t3700 = 0 - summatService.laskeDebetMinusKredit('3700', tilikaudenSummat, affectedAccounts)
        return t3700
      }
    })

    const v335 = this._asetaNumero('335', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        const t3800to3840 = 0 - summatService.laskeDebetMinusKreditTilialueelta(3800, 3840, tilikaudenSummat, affectedAccounts)
        return t3800to3840
      } else {
        const t3800 = 0 - summatService.laskeDebetMinusKredit('3800', tilikaudenSummat, affectedAccounts)
        return t3800
      }
    })

    const v336 = this._asetaNumero('336', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 336 = 3710...3790 + 3850...3990 + 550 + 562 + 565
        const t3710to3790 = 0 - summatService.laskeDebetMinusKreditTilialueelta(3710, 3790, tilikaudenSummat, affectedAccounts)
        const t3850to3990 = 0 - summatService.laskeDebetMinusKreditTilialueelta(3850, 3990, tilikaudenSummat, affectedAccounts)
        const t550 = 0 - summatService.laskeDebetMinusKredit('550', tilikaudenSummat, affectedAccounts)
        const t562 = 0 - summatService.laskeDebetMinusKredit('562', tilikaudenSummat, affectedAccounts)
        const t565 = 0 - summatService.laskeDebetMinusKredit('565', tilikaudenSummat, affectedAccounts)
        return this._sum(t3710to3790, t3850to3990, t550, t562, t565)
      } else {
        // 336 = 3700 + 3800 + 37 + 3801 + 3990
        const t3700 = 0 - summatService.laskeDebetMinusKredit('3700', tilikaudenSummat, affectedAccounts)
        const t3800 = 0 - summatService.laskeDebetMinusKredit('3800', tilikaudenSummat, affectedAccounts)
        const t37 = 0 - summatService.laskeDebetMinusKredit('37', tilikaudenSummat, affectedAccounts)
        const t3801 = 0 - summatService.laskeDebetMinusKredit('3801', tilikaudenSummat, affectedAccounts)
        const t3990 = 0 - summatService.laskeDebetMinusKredit('3990', tilikaudenSummat, affectedAccounts)
        return t37 - t3700 - t3800 - t3801 - t3990
      }
    })

    // Rahoitustuotot
    const v337 = this._asetaNumero('337', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 337 = täytettävä kenttä
        return undefined
      } else {
        // 337 = 9275 + 9276
        const t9275 = 0 - summatService.laskeDebetMinusKredit('9275', tilikaudenSummat, affectedAccounts)
        const t9276 = 0 - summatService.laskeDebetMinusKredit('9276', tilikaudenSummat, affectedAccounts)
        return t9275 + t9276
      }
    })

    const v338 = this._asetaNumero('338', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        return undefined
      } else {
        // 338 = 9275
        const t9275 = 0 - summatService.laskeDebetMinusKredit('9275', tilikaudenSummat, affectedAccounts)
        return t9275
      }
    })
    // 339 = täytettävä
    const v339 = this._asetaNumero('339', arvot)
    // 340 = täytettävä
    const v340 = this._asetaNumero('340', arvot)
    const v341 = this._asetaNumero('341', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 341 = 9170...9230 + 9250
        const t9170to9230 = 0 - summatService.laskeDebetMinusKreditTilialueelta(9170, 9230, tilikaudenSummat, affectedAccounts)
        const t9250 = 0 - summatService.laskeDebetMinusKredit('9250', tilikaudenSummat, affectedAccounts)
        return t9170to9230 + t9250
      } else {
        // 341 = 9200
        const t9200 = 0 - summatService.laskeDebetMinusKredit('9200', tilikaudenSummat, affectedAccounts)
        return t9200
      }
    })
    // 344 = täytettävä
    const v344 = this._asetaNumero('344', arvot)
    const v345 = this._asetaNumero('345', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 345 = 9260 + 9290
        const t9260 = 0 - summatService.laskeDebetMinusKredit('9260', tilikaudenSummat, affectedAccounts)
        const t9290 = 0 - summatService.laskeDebetMinusKredit('9290', tilikaudenSummat, affectedAccounts)
        return t9260 + t9290
      } else {
        // 345 = 9290..9299
        const t9290to9299 = 0 - summatService.laskeDebetMinusKreditTilialueelta(9290, 9299, tilikaudenSummat, affectedAccounts)
        return t9290to9299
      }
    })

    // Muut tuotot
    // 346 = täytettävä
    const v346 = this._asetaNumero('346', arvot)
    // 347 = täytettävä
    const v347 = this._asetaNumero('347', arvot)
    // 439 = täytettävä
    const v439 = this._asetaNumero('439', arvot)
    // 350 = täytettävä
    const v350 = this._asetaNumero('350', arvot)
    // 351 = täytettävä
    const v351 = this._asetaNumero('351', arvot)
    // 261 = 9280
    const v261 = this._asetaNumero('261', arvot, () => {
      const t9280 = 0 - summatService.laskeDebetMinusKredit('9280', tilikaudenSummat, affectedAccounts)
      return t9280
    })
    // 353 = täytettävä
    const v353 = this._asetaNumero('353', arvot)

    // ELINKEINOTOIMINNAN VERONALAISET TUOTOT YHTEENSÄ
    const v354 = this._asetaNumero('354', arvot, () => {
      // console.log(v330, v331, v333, v260, v334, v335, v336, v338, v339, v340, v341, v344, v345, v347, v439, v351, v261, v353)
      return this._sum(v330, v331, v333, v260, v334, v335, v336, v338, v339, v340, v341, v344, v345, v347, v439, v351, v261, v353)
    })

    // Verottomat erät
    // 357 = 9950 jos enemmän kredittiä
    const v357 = this._asetaNumero('357', arvot, () => {
      return Math.max(0, summatService.laskeKreditMinusDebet('9950', tilikaudenSummat, affectedAccounts))
    })
    // 320 = 3801
    const v320 = this._asetaNumero('320', arvot, () => {
      return 0 - summatService.laskeDebetMinusKredit('3801', tilikaudenSummat, affectedAccounts)
    })
    // 318 = täytettävä
    const v318 = this._asetaNumero('318', arvot)
    // 358 = 9240 + 3990
    const v358 = this._asetaNumero('358', arvot, () => {
      const t9240 = 0 - summatService.laskeDebetMinusKredit('9240', tilikaudenSummat, affectedAccounts)
      const t3990 = 0 - summatService.laskeDebetMinusKredit('3990', tilikaudenSummat, affectedAccounts)
      return this._sum(t9240, t3990)
    })

    // Ostot ja varastonmuutokset
    // // 359 = 400 + 440

    const v359 = this._asetaNumero('359', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 359 = 400 + 440 + 35
        const t400 = summatService.laskeDebetMinusKredit('400', tilikaudenSummat, affectedAccounts)
        const t440 = summatService.laskeDebetMinusKredit('440', tilikaudenSummat, affectedAccounts)
        const t35 = summatService.laskeDebetMinusKredit('35', tilikaudenSummat, affectedAccounts)
        return this._sum(t400, t440, t35)
      } else {
        const t400 = summatService.laskeDebetMinusKredit('400', tilikaudenSummat, affectedAccounts)
        const t440 = summatService.laskeDebetMinusKredit('440', tilikaudenSummat, affectedAccounts)
        return this._sum(t400, t440)
      }
    })
    // 360 = 445
    const t445 = summatService.laskeDebetMinusKredit('445', tilikaudenSummat, affectedAccounts)
    const v360 = this._asetaNumero('360', arvot, () => t445)

    // Henkilöstökulut
    const v361 = this._asetaNumero('361', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 361 = 500 + 540 + 560 + 561 + 563 + 564 + 566
        const t500 = summatService.laskeDebetMinusKredit('500', tilikaudenSummat, affectedAccounts)
        const t540 = summatService.laskeDebetMinusKredit('540', tilikaudenSummat, affectedAccounts)
        const t560 = summatService.laskeDebetMinusKredit('560', tilikaudenSummat, affectedAccounts)
        const t561 = summatService.laskeDebetMinusKredit('561', tilikaudenSummat, affectedAccounts)
        const t563 = summatService.laskeDebetMinusKredit('563', tilikaudenSummat, affectedAccounts)
        const t564 = summatService.laskeDebetMinusKredit('564', tilikaudenSummat, affectedAccounts)
        const t566 = summatService.laskeDebetMinusKredit('566', tilikaudenSummat, affectedAccounts)
        return this._sum(t500, t540, t560, t561, t563, t564, t566)
      } else {
        // 361 = 500 + 540
        const t500 = summatService.laskeDebetMinusKredit('500', tilikaudenSummat, affectedAccounts)
        const t540 = summatService.laskeDebetMinusKredit('540', tilikaudenSummat, affectedAccounts)
        return this._sum(t500, t540)
      }
    })
    // 362 = 6100...6240
    const v362 = this._asetaNumero('362', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 362 = 6000...6290
        const t6000to6290 = summatService.laskeDebetMinusKreditTilialueelta(6000, 6290, tilikaudenSummat, affectedAccounts)
        return t6000to6290
      } else {
        // 362 = 6100...6240
        const t6100to6240 = summatService.laskeDebetMinusKreditTilialueelta(6100, 6240, tilikaudenSummat, affectedAccounts)
        return t6100to6240
      }
    })
    const v363 = this._asetaNumero('363', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 363 = 600 - 6000...6290
        const t600 = summatService.laskeDebetMinusKredit('600', tilikaudenSummat, affectedAccounts)
        const t6000to6290 = summatService.laskeDebetMinusKreditTilialueelta(6000, 6290, tilikaudenSummat, affectedAccounts)
        return t600 - t6000to6290
      } else {
        // 363 = 600 - 6100...6240
        const t6100to6240 = summatService.laskeDebetMinusKreditTilialueelta(6100, 6240, tilikaudenSummat, affectedAccounts)
        const t600 = summatService.laskeDebetMinusKredit('600', tilikaudenSummat, affectedAccounts)
        return t600 - t6100to6240
      }
    })

    // Poistot ja käyttöomaisuuden arvonalentumiset
    // 364 = vapaa kenttä (tili 6800...6889)
    const v364 = this._asetaNumero('364', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 364 = vapaa kenttä (tilit 6800...6930)
        const t6800to6930 = summatService.laskeDebetMinusKreditTilialueelta(6800, 6930, tilikaudenSummat, affectedAccounts)
        return t6800to6930
      } else {
        // 364 = vapaa kenttä (tilit 6800...6889)
        const t6800to6889 = summatService.laskeDebetMinusKreditTilialueelta(6800, 6889, tilikaudenSummat, affectedAccounts)
        return t6800to6889
      }
    })
    // 365 = vapaa kenttä(tili 6800...6889)
    const t6800to6889 = summatService.laskeDebetMinusKreditTilialueelta(6800, 6889, tilikaudenSummat, affectedAccounts)
    const v365 = this._asetaNumero('365', arvot, () => t6800to6889)

    // Kentät poistuivat 2025.
    // // 366 = vapaa kenttä
    // const t6960 = summatService.laskeDebetMinusKredit('6960', tilikaudenSummat, affectedAccounts)
    // const v366 = this._asetaNumero('366', arvot, () => t6960)
    // // 367 = vapaa kenttä
    // const v367 = this._asetaNumero('367', arvot, () => t6960)

    // Toiminnan muut kulut
    const v368 = this._asetaNumero('368', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 368 = 7950...7990
        const t7950to7990 = summatService.laskeDebetMinusKreditTilialueelta(7950, 7990, tilikaudenSummat, affectedAccounts)
        return t7950to7990
      } else {
        // 368 = 7990
        const t7990 = summatService.laskeDebetMinusKredit('7990', tilikaudenSummat, affectedAccounts)
        return t7990
      }
    })
    const v369 = this._asetaNumero('369', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 369 = 7950...7990 / 2
        const t7950to7990 = summatService.laskeDebetMinusKreditTilialueelta(7950, 7990, tilikaudenSummat, affectedAccounts)
        return t7950to7990 / 2
      } else {
        // 369 = 7990 / 2
        const t7990 = summatService.laskeDebetMinusKredit('7990', tilikaudenSummat, affectedAccounts)
        return t7990 / 2
      }
    })
    // 370 = 8280 + 8285
    const t8280 = summatService.laskeDebetMinusKredit('8280', tilikaudenSummat, affectedAccounts)
    const t8285 = summatService.laskeDebetMinusKredit('8285', tilikaudenSummat, affectedAccounts)
    // console.log('v370 = t8280 + t8285 (' + t8280 + ' + ' + t8285 + ')')
    const v370 = this._asetaNumero('370', arvot, () => this._sum(t8280, t8285))
    // 371 = 8285
    const v371 = this._asetaNumero('371', arvot, () => t8285)
    // 372 = vapaa kenttä(jos arvoa, näytä teksti: erittele omaverossa lomakkeella 71A ja / tai 71B)
    const v372 = this._asetaNumero('372', arvot)
    // 373 = vapaa kenttä(jos arvoa, näytä teksti: erittele omaverossa lomakkeella 71A ja / tai 71B)
    const v373 = this._asetaNumero('373', arvot)
    // 374 = 8850
    const t8850 = summatService.laskeDebetMinusKredit('8850', tilikaudenSummat, affectedAccounts)
    const v374 = this._asetaNumero('374', arvot, () => t8850)
    const v375 = this._asetaNumero('375', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 375 = 7510 + 7560 + 7640 + 7710
        const t7510 = summatService.laskeDebetMinusKredit('7510', tilikaudenSummat, affectedAccounts)
        const t7560 = summatService.laskeDebetMinusKredit('7560', tilikaudenSummat, affectedAccounts)
        const t7640 = summatService.laskeDebetMinusKredit('7640', tilikaudenSummat, affectedAccounts)
        const t7710 = summatService.laskeDebetMinusKredit('7710', tilikaudenSummat, affectedAccounts)
        return this._sum(t7510, t7560, t7640, t7710)
      } else {
        // 375 = 7560 + 7710
        const t7560 = summatService.laskeDebetMinusKredit('7560', tilikaudenSummat, affectedAccounts)
        const t7710 = summatService.laskeDebetMinusKredit('7710', tilikaudenSummat, affectedAccounts)
        return this._sum(t7560, t7710)
      }
    })
    // 376 = täytettävä kenttä
    const v376 = this._asetaNumero('376', arvot)

    const t8770 = summatService.laskeDebetMinusKredit('8770', tilikaudenSummat, affectedAccounts)
    const t8800 = summatService.laskeDebetMinusKredit('8800', tilikaudenSummat, affectedAccounts)
    const t8860 = summatService.laskeDebetMinusKredit('8860', tilikaudenSummat, affectedAccounts)
    const t8780 = summatService.laskeDebetMinusKredit('8780', tilikaudenSummat, affectedAccounts)
    // console.log(t70, '-', t7560, '-', t7710, '-', t7990, '-', t8280, '-', t8285, '-', t8770, '-', t8780, '-', t8800, '-', t8850, '-', t8860, '=', t70 - t7560 - t7710 - t7990 - t8280 - t8285 - t8770 - t8780 - t8800 - t8850 - t8860)
    const v377 = this._asetaNumero('377', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 377 = 70 - 7510 - 7560 - 7640 - 7710 - 7950...7990 - 8280 - 8285 - 8770 - 8780 - 8800 - 8850 - 8860
        const t70 = summatService.laskeDebetMinusKredit('70', tilikaudenSummat, affectedAccounts)
        const t7510 = summatService.laskeDebetMinusKredit('7510', tilikaudenSummat, affectedAccounts)
        const t7560 = summatService.laskeDebetMinusKredit('7560', tilikaudenSummat, affectedAccounts)
        const t7640 = summatService.laskeDebetMinusKredit('7640', tilikaudenSummat, affectedAccounts)
        const t7710 = summatService.laskeDebetMinusKredit('7710', tilikaudenSummat, affectedAccounts)
        const t7950to7990 = summatService.laskeDebetMinusKreditTilialueelta(7950, 7990, tilikaudenSummat, affectedAccounts)
        return t70 - t7510 - t7560 - t7640 - t7710 - t7950to7990 - t8280 - t8285 - t8770 - t8780 - t8800 - t8850 - t8860
      } else {
        const t70 = summatService.laskeDebetMinusKredit('70', tilikaudenSummat, affectedAccounts)
        const t7560 = summatService.laskeDebetMinusKredit('7560', tilikaudenSummat, affectedAccounts)
        const t7710 = summatService.laskeDebetMinusKredit('7710', tilikaudenSummat, affectedAccounts)
        const t7990 = summatService.laskeDebetMinusKredit('7990', tilikaudenSummat, affectedAccounts)
        // 377 = 70 - 7560 - 7710 - 7990 - 8280 - 8285 - 8770 - 8780 - 8800 - 8850 - 8860
        return t70 - t7560 - t7710 - t7990 - t8280 - t8285 - t8770 - t8780 - t8800 - t8850 - t8860
      }
    })
    // Vähennyskelvottomat kulut
    // 378 = 99 - 9950(jos enemmän kredittiä)
    const t99 = summatService.laskeDebetMinusKredit('99', tilikaudenSummat, affectedAccounts)
    const t9950speciale = Math.max(0, summatService.laskeKreditMinusDebet('9950', tilikaudenSummat, affectedAccounts))
    // console.log('t99', t99, 't9950speciale', t9950speciale)
    const v378 = this._asetaNumero('378', arvot, () => t99 + t9950speciale)
    // const v378 = this._asetaNumero('378', arvot, () => t99)
    // 379 = 8770 + 9540
    const t9540 = summatService.laskeDebetMinusKredit('9540', tilikaudenSummat, affectedAccounts)
    const v379 = this._asetaNumero('379', arvot, () => this._sum(t8770, t9540))
    // 380 = 8780
    const v380 = this._asetaNumero('380', arvot, () => t8780)
    // 381 = täytettävä kenttä
    const v381 = this._asetaNumero('381', arvot)
    // 382 = täytettävä kenttä
    const v382 = this._asetaNumero('382', arvot)
    // 383 = täytettävä kenttä
    const v383 = this._asetaNumero('383', arvot)
    // 384 = 8800
    const v384 = this._asetaNumero('384', arvot, () => t8800)

    // Rahoituskulut
    // 328 = täytettävä kenttä
    const v328 = this._asetaNumero('328', arvot)
    // 386 = täytettävä kenttä
    const v386 = this._asetaNumero('386', arvot)
    const v387 = this._asetaNumero('387', arvot, () => {
      // 387 = 9420...9539 + 9541...9550
      if (tilikarttaProfiili === 'liikekirjuri') {
        const t9420to9539 = summatService.laskeDebetMinusKreditTilialueelta(9420, 9539, tilikaudenSummat, affectedAccounts)
        const t9541to9550 = summatService.laskeDebetMinusKreditTilialueelta(9541, 9550, tilikaudenSummat, affectedAccounts)
        return t9420to9539 + t9541to9550
      } else {
        // 387 = 9500
        const t9500 = summatService.laskeDebetMinusKredit('9500', tilikaudenSummat, affectedAccounts)
        return t9500
      }
    })
    // 398 = täytettävä kenttä
    const v398 = this._asetaNumero('398', arvot)
    // 399 = täytettävä kenttä
    const v399 = this._asetaNumero('399', arvot)
    // 388 = täytettävä kenttä
    const v388 = this._asetaNumero('388', arvot)
    // 389 = täytettävä kenttä
    const v389 = this._asetaNumero('389', arvot)
    // 390 = täytettävä kenttä
    const v390 = this._asetaNumero('390', arvot)
    // 391 = täytettävä kenttä
    const v391 = this._asetaNumero('391', arvot)

    const v392 = this._asetaNumero('392', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 392 = 9560...9688 + 9690
        const t9560to9688 = summatService.laskeDebetMinusKreditTilialueelta(9560, 9688, tilikaudenSummat, affectedAccounts)
        const t9690 = summatService.laskeDebetMinusKredit('9690', tilikaudenSummat, affectedAccounts)
        return t9560to9688 + t9690
      } else {
        // 392 = 9600
        const t9600 = summatService.laskeDebetMinusKredit('9600', tilikaudenSummat, affectedAccounts)
        return t9600
      }
    })

    // Muut kulut
    // 327 = täytettävä kenttä
    const v327 = this._asetaNumero('327', arvot)
    // 436 = täytettävä kenttä
    const v436 = this._asetaNumero('436', arvot)
    //   (jos arvoa, näytä teksti: erittele omaverossa lomakkeella 65)
    // 394 = täytettävä kenttä
    const t9870 = summatService.laskeDebetMinusKredit('9870', tilikaudenSummat, affectedAccounts)
    const v394 = this._asetaNumero('394', arvot, () => t9870)
    // 395 = täytettävä kenttä
    const v395 = this._asetaNumero('395', arvot, () => t9870)
    // 270 = 8860 + 9580
    const v270 = this._asetaNumero('270', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        const t9689 = summatService.laskeDebetMinusKredit('9689', tilikaudenSummat, affectedAccounts)
        return t9689
      } else {
        const t9580 = summatService.laskeDebetMinusKredit('9580', tilikaudenSummat, affectedAccounts)
        return t8860 + t9580
      }
    })
    //   (jos arvoa, näytä teksti: erittele omaverossa lomakkeella 71B)
    // 271 = täytettävä kenttä
    const v271 = this._asetaNumero('271', arvot, () => t8860)
    // 272 = täytettävä kenttä
    const v272 = this._asetaNumero('272', arvot)
    // 273 = täytettävä kenttä
    const v273 = this._asetaNumero('273', arvot)

    // 695 Tutkimus- ja kehittämistoiminnan väliaikainen lisävähennys G13,2
    const v695 = this._asetaNumero('695', arvot)
    // 697 Tutkimus- ja kehittämistoiminnan yleinen lisävähennys G13,2
    const v697 = this._asetaNumero('697', arvot)
    // 698 Tutkimus- ja kehittämistoiminnan ylimääräinen lisävähennys (erittele lomakkeella 67Y) G13,2
    const v698 = this._asetaNumero('698', arvot)
    // 696 Ulkomaisen tytäryhtiön tappiot (konsernivähennys) - anna selvitys vapaamuotoisella liitteellä G13,2
    const v696 = this._asetaNumero('696', arvot)

    // 396 = täytettävä kenttä
    const v396 = this._asetaNumero('396', arvot)
    // 504 = täytettävä kenttä
    const v504 = this._asetaNumero('504', arvot)

    const v397 = this._asetaNumero('397', arvot, () =>
      this._sum(
        v359,
        v360,
        v361,
        v362,
        v363,
        v365,
        v369,
        v371,
        v373,
        v374,
        v375,
        v376,
        v377,
        v328,
        v386,
        v387,
        v398,
        v399,
        v390,
        v391,
        v392,
        v327,
        v436,
        v395,
        v271,
        v273,
        v695,
        v697,
        v698,
        v696,
        v396,
      ),
    )

    // Elinekeinotoiminnan tulos
    // 500 = 354 - 397(jos positiivinen)
    // 505 = 354 - 397(jos negatiivinen)
    const tulos = v354 - v397
    oletukset['elinkeinotoiminnan-tulos'] = tulos
    if (tulos > 0) {
      arvot.push({ key: '500', value: this._annaLuku(Math.abs(tulos)) })
    } else if (tulos < 0) {
      arvot.push({ key: '505', value: this._annaLuku(Math.abs(tulos)) })
    } else {
      arvot.push({ key: '500', value: this._annaLuku(0) })
      arvot.push({ key: '505', value: this._annaLuku(0) })
    }

    // Käyttöomaisuus
    const v400 = this._asetaNumero('400', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 400 = 1031...1049 + 1071...1089
        const t1031to1049 = summatService.laskeDebetMinusKreditTilialueelta(1031, 1049, tilikaudenSummat, affectedAccounts)
        const t1071to1089 = summatService.laskeDebetMinusKreditTilialueelta(1071, 1089, tilikaudenSummat, affectedAccounts)
        return t1031to1049 + t1071to1089
      } else {
        // 400 = 1030
        const t1030 = summatService.laskeDebetMinusKredit('1030', tilikaudenSummat, affectedAccounts)
        return t1030
      }
    })
    const v401 = this._asetaNumero('401', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 401 = 1020 + 1050...1061
        const t1020 = summatService.laskeDebetMinusKredit('1020', tilikaudenSummat, affectedAccounts)
        const t1050to1061 = summatService.laskeDebetMinusKreditTilialueelta(1050, 1061, tilikaudenSummat, affectedAccounts)
        return t1020 + t1050to1061
      } else {
        // 401 = 100 - 1030 - 1090
        const t100 = summatService.laskeDebetMinusKredit('100', tilikaudenSummat, affectedAccounts)
        const t1030 = summatService.laskeDebetMinusKredit('1030', tilikaudenSummat, affectedAccounts)
        const t1090 = summatService.laskeDebetMinusKredit('1090', tilikaudenSummat, affectedAccounts)
        return t100 - t1030 - t1090
      }
    })
    const v402 = this._asetaNumero('402', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 402 = 112 + 114
        const t112 = summatService.laskeDebetMinusKredit('112', tilikaudenSummat, affectedAccounts)
        const t114 = summatService.laskeDebetMinusKredit('114', tilikaudenSummat, affectedAccounts)
        return this._sum(t112, t114)
      } else {
        // 402 = 1110 + 1120
        const t1110 = summatService.laskeDebetMinusKredit('1110', tilikaudenSummat, affectedAccounts)
        const t1120 = summatService.laskeDebetMinusKredit('1120', tilikaudenSummat, affectedAccounts)
        return this._sum(t1110, t1120)
      }
    })
    // 403 = 116
    const t116 = summatService.laskeDebetMinusKredit('116', tilikaudenSummat, affectedAccounts)
    const v403 = this._asetaNumero('403', arvot, () => t116)
    const v404 = this._asetaNumero('404', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 404 = 105 + 118
        const t105 = summatService.laskeDebetMinusKredit('105', tilikaudenSummat, affectedAccounts)
        const t118 = summatService.laskeDebetMinusKredit('118', tilikaudenSummat, affectedAccounts)
        return this._sum(t105, t118)
      } else {
        // 404 = 1380 + 1090
        const t1380 = summatService.laskeDebetMinusKredit('1380', tilikaudenSummat, affectedAccounts)
        const t1090 = summatService.laskeDebetMinusKredit('1090', tilikaudenSummat, affectedAccounts)
        return this._sum(t1380, t1090)
      }
    })
    const v405 = this._asetaNumero('405', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 405 = 1480 + 141 + 143
        const t1480 = summatService.laskeDebetMinusKredit('1480', tilikaudenSummat, affectedAccounts)
        const t141 = summatService.laskeDebetMinusKredit('141', tilikaudenSummat, affectedAccounts)
        const t143 = summatService.laskeDebetMinusKredit('143', tilikaudenSummat, affectedAccounts)
        return this._sum(t1480, t141, t143)
      } else {
        // 405 = 1440 + 1400 + 1420
        const t1400 = summatService.laskeDebetMinusKredit('1400', tilikaudenSummat, affectedAccounts)
        const t1420 = summatService.laskeDebetMinusKredit('1420', tilikaudenSummat, affectedAccounts)
        const t1440 = summatService.laskeDebetMinusKredit('1440', tilikaudenSummat, affectedAccounts)
        return this._sum(t1400, t1420, t1440)
      }
    })
    // 406 = täytettävä kenttä
    const v406 = this._asetaNumero('406', arvot)
    const v407 = this._asetaNumero('407', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 407 = 1430 + 144
        const t1430 = summatService.laskeDebetMinusKredit('1430', tilikaudenSummat, affectedAccounts)
        const t144 = summatService.laskeDebetMinusKredit('144', tilikaudenSummat, affectedAccounts)
        return this._sum(t1430, t144)
      } else {
        // 407 = täytettävä kenttä
        return undefined
      }
    })

    const v408 = this._asetaNumero('408', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 408 = 146
        const t146 = summatService.laskeDebetMinusKredit('146', tilikaudenSummat, affectedAccounts)
        return t146
      } else {
        // 408 = täytettävä kenttä
        return undefined
      }
    })
    // 409 = täytettävä kenttä
    const t1300 = summatService.laskeDebetMinusKredit('1300', tilikaudenSummat, affectedAccounts)
    const v409 = this._asetaNumero('409', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 409 = 117
        const t117 = summatService.laskeDebetMinusKredit('117', tilikaudenSummat, affectedAccounts)
        return t117
      } else {
        // 409 = 1300
        return t1300
      }
    })

    const v410 = this._asetaNumero('410', arvot, () => this._sum(v400, v401, v402, v403, v404, v405, v406, v407, v408, v409))

    // Vaihto-omaisuus
    const v411 = this._asetaNumero('411', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 411 = 1500...1501
        const t1500to1501 = summatService.laskeDebetMinusKreditTilialueelta(1500, 1501, tilikaudenSummat, affectedAccounts)
        return t1500to1501
      } else {
        // 411 = 1500
        const t1500 = summatService.laskeDebetMinusKredit('1500', tilikaudenSummat, affectedAccounts)
        return t1500
      }
    })
    const v412 = this._asetaNumero('412', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 412 = 1510...1511
        const t1510to1511 = summatService.laskeDebetMinusKreditTilialueelta(1510, 1511, tilikaudenSummat, affectedAccounts)
        return t1510to1511
      } else {
        // 412 = 1510
        const t1510 = summatService.laskeDebetMinusKredit('1510', tilikaudenSummat, affectedAccounts)
        return t1510
      }
    })
    const v413 = this._asetaNumero('413', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 413 = 1520...1521
        const t1520to1521 = summatService.laskeDebetMinusKreditTilialueelta(1520, 1521, tilikaudenSummat, affectedAccounts)
        return t1520to1521
      } else {
        // 413 = 1520
        const t1520 = summatService.laskeDebetMinusKredit('1520', tilikaudenSummat, affectedAccounts)
        return t1520
      }
    })
    const v414 = this._asetaNumero('414', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 414 = 1530...1533 + 1553
        const t1530to1533 = summatService.laskeDebetMinusKreditTilialueelta(1530, 1533, tilikaudenSummat, affectedAccounts)
        const t1553 = summatService.laskeDebetMinusKredit('1553', tilikaudenSummat, affectedAccounts)
        return t1530to1533 + t1553
      } else {
        // 414 = 1530 + 1553
        const t1530 = summatService.laskeDebetMinusKredit('1530', tilikaudenSummat, affectedAccounts)
        const t1553 = summatService.laskeDebetMinusKredit('1553', tilikaudenSummat, affectedAccounts)
        return this._sum(t1530, t1553)
      }
    })
    // 415 = 1541
    const t1541 = summatService.laskeDebetMinusKredit('1541', tilikaudenSummat, affectedAccounts)
    const v415 = this._asetaNumero('415', arvot, () => t1541)
    // 416 = 1543
    const t1543 = summatService.laskeDebetMinusKredit('1543', tilikaudenSummat, affectedAccounts)
    const v416 = this._asetaNumero('416', arvot, () => t1543)
    const v417 = this._asetaNumero('417', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 417 = 1540 + 1547 + 1549...1551
        const t1540 = summatService.laskeDebetMinusKredit('1540', tilikaudenSummat, affectedAccounts)
        const t1547 = summatService.laskeDebetMinusKredit('1547', tilikaudenSummat, affectedAccounts)
        const t1549to1551 = summatService.laskeDebetMinusKreditTilialueelta(1549, 1551, tilikaudenSummat, affectedAccounts)
        return t1540 + t1547 + t1549to1551
      } else {
        // 417 = 1540 + 1550
        const t1540 = summatService.laskeDebetMinusKredit('1540', tilikaudenSummat, affectedAccounts)
        const t1550 = summatService.laskeDebetMinusKredit('1550', tilikaudenSummat, affectedAccounts)
        return this._sum(t1540, t1550)
      }
    })
    // 418 = 411 + 412 + 413 + 414 + 415 + 416 + 417
    const v418 = this._asetaNumero('418', arvot, () => this._sum(v411, v412, v413, v414, v415, v416, v417))

    // Rahoitusomaisuus
    const v419 = this._asetaNumero('419', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 419 = 1600...1623 + 171
        const t1600to1623 = summatService.laskeDebetMinusKreditTilialueelta(1600, 1623, tilikaudenSummat, affectedAccounts)
        const t171 = summatService.laskeDebetMinusKredit('171', tilikaudenSummat, affectedAccounts)
        return t1600to1623 + t171
      } else {
        // 419 = 1600 + 171
        const t1600 = summatService.laskeDebetMinusKredit('1600', tilikaudenSummat, affectedAccounts)
        const t171 = summatService.laskeDebetMinusKredit('171', tilikaudenSummat, affectedAccounts)
        return this._sum(t171, t1600)
      }
    })
    const v420 = this._asetaNumero('420', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 420 = 1630-1639 + 173
        const t1630to1639 = summatService.laskeDebetMinusKreditTilialueelta(1630, 1639, tilikaudenSummat, affectedAccounts)
        const t173 = summatService.laskeDebetMinusKredit('173', tilikaudenSummat, affectedAccounts)
        return t1630to1639 + t173
      } else {
        return undefined
      }
    })

    const v421 = this._asetaNumero('421', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 421 = 1640-1649 + 174
        const t1640to1649 = summatService.laskeDebetMinusKreditTilialueelta(1640, 1649, tilikaudenSummat, affectedAccounts)
        const t174 = summatService.laskeDebetMinusKredit('174', tilikaudenSummat, affectedAccounts)
        return t1640to1649 + t174
      } else {
        return undefined
      }
    })
    const v422 = this._asetaNumero('422', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 422 = 1650...1659 + 175
        const t1650to1659 = summatService.laskeDebetMinusKreditTilialueelta(1650, 1659, tilikaudenSummat, affectedAccounts)
        const t175 = summatService.laskeDebetMinusKredit('175', tilikaudenSummat, affectedAccounts)
        return t1650to1659 + t175
      } else {
        // 422 = 1650 + 175 - 1755
        const t1650 = summatService.laskeDebetMinusKredit('1650', tilikaudenSummat, affectedAccounts)
        const t175 = summatService.laskeDebetMinusKredit('175', tilikaudenSummat, affectedAccounts)
        const t1755 = summatService.laskeDebetMinusKredit('1755', tilikaudenSummat, affectedAccounts)
        return this._sum(t175, t1650) - t1755
      }
    })
    const v423 = this._asetaNumero('423', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 423 = 1660...1671 + 176
        const t1660to1671 = summatService.laskeDebetMinusKreditTilialueelta(1660, 1671, tilikaudenSummat, affectedAccounts)
        const t176 = summatService.laskeDebetMinusKredit('176', tilikaudenSummat, affectedAccounts)
        return t1660to1671 + t176
      } else {
        // 423 = 176 + 1660 + 1667
        const t1660 = summatService.laskeDebetMinusKredit('1660', tilikaudenSummat, affectedAccounts)
        const t1667 = summatService.laskeDebetMinusKredit('1667', tilikaudenSummat, affectedAccounts)
        const t176 = summatService.laskeDebetMinusKredit('176', tilikaudenSummat, affectedAccounts)
        return this._sum(t176, t1660, t1667)
      }
    })
    const v424 = this._asetaNumero('424', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 424 = 178 + 186
        const t178 = summatService.laskeDebetMinusKredit('178', tilikaudenSummat, affectedAccounts)
        const t186 = summatService.laskeDebetMinusKredit('186', tilikaudenSummat, affectedAccounts)
        return this._sum(t178, t186)
      } else {
        // 424 = 1860 + 1890
        const t1860 = summatService.laskeDebetMinusKredit('1860', tilikaudenSummat, affectedAccounts)
        const t1890 = summatService.laskeDebetMinusKredit('1890', tilikaudenSummat, affectedAccounts)
        return this._sum(t1860, t1890)
      }
    })
    const v425 = this._asetaNumero('425', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 425 = 1680...1691 + 180
        const t1680to1691 = summatService.laskeDebetMinusKreditTilialueelta(1680, 1691, tilikaudenSummat, affectedAccounts)
        const t180 = summatService.laskeDebetMinusKredit('180', tilikaudenSummat, affectedAccounts)
        return t1680to1691 + t180
      } else {
        // 425 = 1680 + 180
        const t1680 = summatService.laskeDebetMinusKredit('1680', tilikaudenSummat, affectedAccounts)
        const t180 = summatService.laskeDebetMinusKredit('180', tilikaudenSummat, affectedAccounts)
        return this._sum(t1680, t180)
      }
    })
    // 463 = täytettävä kenttä
    const v463 = this._asetaNumero('463', arvot)
    const v426 = this._asetaNumero('426', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 426 = 1900...1905
        const t1900to1905 = summatService.laskeDebetMinusKreditTilialueelta(1900, 1905, tilikaudenSummat, affectedAccounts)
        return t1900to1905
      } else {
        // 426 = 1900...1909
        const t1900to1909 = summatService.laskeDebetMinusKreditTilialueelta(1900, 1909, tilikaudenSummat, affectedAccounts)
        return t1900to1909
      }
    })
    // 427 = 1910...1999
    const t1910to1999 = summatService.laskeDebetMinusKreditTilialueelta(1910, 1999, tilikaudenSummat, affectedAccounts)
    const v427 = this._asetaNumero('427', arvot, () => t1910to1999)
    // 428 = täytettävä kenttä
    const v428 = this._asetaNumero('428', arvot)
    // 429 = 419 + 420 + 421 + 422 + 423 + 424 + 425 + 426 + 427 + 428 + v463
    const v429 = this._asetaNumero('429', arvot, () => this._sum(v419, v420, v421, v422, v423, v424, v425, v426, v427, v428, v463))

    // Muu omaisuus
    const v430 = this._asetaNumero('430', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 430 = 145
        const t145 = summatService.laskeDebetMinusKredit('145', tilikaudenSummat, affectedAccounts)
        return t145
      } else {
        // 430 = 1450
        const t1450 = summatService.laskeDebetMinusKredit('1450', tilikaudenSummat, affectedAccounts)
        return t1450
      }
    })
    // 431 = täytettävä kenttä
    const v431 = this._asetaNumero('431', arvot)
    // 432 = 1755
    const t1755 = summatService.laskeDebetMinusKredit('1755', tilikaudenSummat, affectedAccounts)
    const v432 = this._asetaNumero('432', arvot, () => t1755)

    const v437 = this._asetaNumero('437', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 437 = 142
        const t142 = summatService.laskeDebetMinusKredit('142', tilikaudenSummat, affectedAccounts)
        return t142
      } else {
        return undefined
      }
    })
    // 438 = täytettävä kenttä
    const v438 = this._asetaNumero('438', arvot)
    // 433 = täytettävä kenttä
    const v433 = this._asetaNumero('433', arvot)
    // 434 = 430 + 431 + 432 + 437 + 438 + 433
    const v434 = this._asetaNumero('434', arvot, () => this._sum(v430, v431, v432, v437, v438, v433))
    // VARAT YHTEENSÄ
    // 435 = 410 + 418 + 429 + 434
    const v435 = this._asetaNumero('435', arvot, () => this._sum(v410, v418, v429, v434))

    // Velat
    const v450 = this._asetaNumero('450', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 450 = 265
        const t265 = 0 - summatService.laskeDebetMinusKredit('265', tilikaudenSummat, affectedAccounts)
        return t265
      } else {
        return undefined
      }
    })
    const v451 = this._asetaNumero('451', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 451 = 263 + 283
        const t263 = 0 - summatService.laskeDebetMinusKredit('263', tilikaudenSummat, affectedAccounts)
        const t283 = 0 - summatService.laskeDebetMinusKredit('283', tilikaudenSummat, affectedAccounts)
        return this._sum(t263, t283)
      } else {
        return undefined
      }
    })
    // 452 = 262 + 282
    const t262 = 0 - summatService.laskeDebetMinusKredit('262', tilikaudenSummat, affectedAccounts)
    const t282 = 0 - summatService.laskeDebetMinusKredit('282', tilikaudenSummat, affectedAccounts)
    const v452 = this._asetaNumero('452', arvot, () => this._sum(t262, t282))
    // 453 = 267 + 287
    const t267 = 0 - summatService.laskeDebetMinusKredit('267', tilikaudenSummat, affectedAccounts)
    const t287 = 0 - summatService.laskeDebetMinusKredit('287', tilikaudenSummat, affectedAccounts)
    const v453 = this._asetaNumero('453', arvot, () => this._sum(t267, t287))
    // 454 = 270 + 290
    const t270 = 0 - summatService.laskeDebetMinusKredit('270', tilikaudenSummat, affectedAccounts)
    const t290 = 0 - summatService.laskeDebetMinusKredit('290', tilikaudenSummat, affectedAccounts)
    const v454 = this._asetaNumero('454', arvot, () => this._sum(t270, t290))
    // 455 = 271 + 291
    const t271 = 0 - summatService.laskeDebetMinusKredit('271', tilikaudenSummat, affectedAccounts)
    const t291 = 0 - summatService.laskeDebetMinusKredit('291', tilikaudenSummat, affectedAccounts)
    const v455 = this._asetaNumero('455', arvot, () => this._sum(t271, t291))
    // 456
    const v456 = this._asetaNumero('456', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 456 = 2941-2944
        const t2941to2944 = 0 - summatService.laskeDebetMinusKreditTilialueelta(2941, 2944, tilikaudenSummat, affectedAccounts)
        return t2941to2944
      } else {
        // 456 = 2940-2945
        const t2940to2945 = 0 - summatService.laskeDebetMinusKreditTilialueelta(2940, 2945, tilikaudenSummat, affectedAccounts)
        return t2940to2945
      }
    })
    // 457 = 276 + 295
    const t276 = 0 - summatService.laskeDebetMinusKredit('276', tilikaudenSummat, affectedAccounts)
    const t295 = 0 - summatService.laskeDebetMinusKredit('295', tilikaudenSummat, affectedAccounts)
    const v457 = this._asetaNumero('457', arvot, () => this._sum(t276, t295))
    // 464 = 264
    const t264 = 0 - summatService.laskeDebetMinusKredit('264', tilikaudenSummat, affectedAccounts)
    const v464 = this._asetaNumero('464', arvot, () => t264)
    // 465 = 286
    const t286 = 0 - summatService.laskeDebetMinusKredit('286', tilikaudenSummat, affectedAccounts)
    const v465 = this._asetaNumero('465', arvot, () => t286)
    // 458: Profile-specific
    const v458 = this._asetaNumero('458', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 458 = 275 + 277 + 285 + 289 + 268 + 292 - 2941...2944
        const t275 = 0 - summatService.laskeDebetMinusKredit('275', tilikaudenSummat, affectedAccounts)
        const t277 = 0 - summatService.laskeDebetMinusKredit('277', tilikaudenSummat, affectedAccounts)
        const t285 = 0 - summatService.laskeDebetMinusKredit('285', tilikaudenSummat, affectedAccounts)
        const t289 = 0 - summatService.laskeDebetMinusKredit('289', tilikaudenSummat, affectedAccounts)
        const t268 = 0 - summatService.laskeDebetMinusKredit('268', tilikaudenSummat, affectedAccounts)
        const t292 = 0 - summatService.laskeDebetMinusKredit('292', tilikaudenSummat, affectedAccounts)
        const t2941to2944 = 0 - summatService.laskeDebetMinusKreditTilialueelta(2941, 2944, tilikaudenSummat, affectedAccounts)
        return this._sum(t275, t277, t285, t289, t268, t292) - t2941to2944
      } else {
        // 458 = 275 + 292 - 2940...2945
        const t275 = 0 - summatService.laskeDebetMinusKredit('275', tilikaudenSummat, affectedAccounts)
        const t292 = 0 - summatService.laskeDebetMinusKredit('292', tilikaudenSummat, affectedAccounts)
        const t2940to2945 = 0 - summatService.laskeDebetMinusKreditTilialueelta(2940, 2945, tilikaudenSummat, affectedAccounts)
        return this._sum(t275, t292) - t2940to2945
      }
    })
    // 459 = 261 + 281 + 238
    const t261 = 0 - summatService.laskeDebetMinusKredit('261', tilikaudenSummat, affectedAccounts)
    const t281 = 0 - summatService.laskeDebetMinusKredit('281', tilikaudenSummat, affectedAccounts)
    const t238 = 0 - summatService.laskeDebetMinusKredit('238', tilikaudenSummat, affectedAccounts)
    const v459 = this._asetaNumero('459', arvot, () => this._sum(t261, t281, t238))
    // VELAT YHTEENSÄ
    // 460 = 450 + 451 + 452 + 453 + 454 + 455 + 456 + 457 + 464 + 465 + 458 + 459
    const v460 = this._asetaNumero('460', arvot, () => this._sum(v450, v451, v452, v453, v454, v455, v456, v457, v464, v465, v458, v459))
    // 461 = 280
    const t280 = 0 - summatService.laskeDebetMinusKredit('280', tilikaudenSummat, affectedAccounts, undefined, ['2830'])
    const v461 = this._asetaNumero('461', arvot, () => t280)
    // 462 = 260
    const t260 = 0 - summatService.laskeDebetMinusKredit('260', tilikaudenSummat, affectedAccounts, undefined, ['2630'])
    const v462 = this._asetaNumero('462', arvot, () => t260)

    // 573 = 435 - 460(jos positiivinen)
    // 574 = 435 - 460(jos negatiivinen)
    const nettovarallisuus = v435 - v460
    oletukset['nettovarallisuus'] = nettovarallisuus
    if (nettovarallisuus < 0) {
      arvot.push({ key: '574', value: this._annaLuku(Math.abs(nettovarallisuus)) })
    } else if (nettovarallisuus > 0) {
      arvot.push({ key: '573', value: this._annaLuku(Math.abs(nettovarallisuus)) })
    } else {
      arvot.push({ key: '574', value: this._annaLuku(0) })
      arvot.push({ key: '573', value: this._annaLuku(0) })
    }

    // Oma pääoma
    // Sidottu oma pääoma
    // 470 = 200
    const t200 = 0 - summatService.laskeDebetMinusKredit('200', tilikaudenSummat, affectedAccounts)
    const v470 = this._asetaNumero('470', arvot, () => t200)
    const v495 = this._asetaNumero('495', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 495 = 205 + 210 + 215
        const t205 = 0 - summatService.laskeDebetMinusKredit('205', tilikaudenSummat, affectedAccounts)
        const t210 = 0 - summatService.laskeDebetMinusKredit('210', tilikaudenSummat, affectedAccounts)
        const t215 = 0 - summatService.laskeDebetMinusKredit('215', tilikaudenSummat, affectedAccounts)
        return this._sum(t205, t210, t215)
      } else {
        // 495= 210 + 215
        const t210 = 0 - summatService.laskeDebetMinusKredit('210', tilikaudenSummat, affectedAccounts)
        const t215 = 0 - summatService.laskeDebetMinusKredit('215', tilikaudenSummat, affectedAccounts)
        return this._sum(t210, t215)
      }
    })
    // Vapaa oma pääoma
    const v496 = this._asetaNumero('496', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 496 = 2060 + 2061
        const t2060 = 0 - summatService.laskeDebetMinusKredit('2060', tilikaudenSummat, affectedAccounts)
        const t2061 = 0 - summatService.laskeDebetMinusKredit('2061', tilikaudenSummat, affectedAccounts)
        return this._sum(t2060, t2061)
      } else {
        // 496 = 2060
        const t2060 = 0 - summatService.laskeDebetMinusKredit('2060', tilikaudenSummat, affectedAccounts)
        return t2060
      }
    })
    const v497 = this._asetaNumero('497', arvot, () => {
      if (tilikarttaProfiili === 'liikekirjuri') {
        // 497 = 2070...2120
        const t2070to2120 = 0 - summatService.laskeDebetMinusKreditTilialueelta(2070, 2120, tilikaudenSummat, affectedAccounts)
        return t2070to2120
      } else {
        // 497 = 220 - 2060
        const t220 = 0 - summatService.laskeDebetMinusKredit('220', tilikaudenSummat, affectedAccounts)
        const t2060 = 0 - summatService.laskeDebetMinusKredit('2060', tilikaudenSummat, affectedAccounts)
        return t220 - t2060
      }
    })
    // 474 = 225(jos kredit)
    // 475 = 225(jos debet)
    const t225 = summatService.laskeDebetMinusKredit('225', tilikaudenSummat, affectedAccounts)
    let v474 = 0
    let v475 = 0
    if (t225 < 0) {
      v474 = this._asetaNumero('474', arvot, () => Math.abs(t225))
    } else if (t225 > 0) {
      v475 = this._asetaNumero('475', arvot, () => Math.abs(t225))
    } else {
      v474 = this._asetaNumero('474', arvot, () => Math.abs(0))
      v475 = this._asetaNumero('475', arvot, () => Math.abs(0))
    }

    // 476 = Kirjanpidon voitto
    // 477 = Kirjanpidon tappio
    const t3 = summatService.laskeDebetMinusKredit('3', tilikaudenSummat, ignoredAccounts)
    let v476 = 0
    let v477 = 0
    if (t3 < 0) {
      v476 = this._asetaNumero('476', arvot, () => Math.abs(t3))
    } else if (t3 > 0) {
      v477 = this._asetaNumero('477', arvot, () => Math.abs(t3))
    } else {
      v476 = this._asetaNumero('476', arvot, () => Math.abs(0))
      v477 = this._asetaNumero('477', arvot, () => Math.abs(0))
    }

    // OMA PÄÄOMA YHTEENSÄ
    // 478 = 20 + 2370
    const t20 = 0 - summatService.laskeDebetMinusKredit('20', tilikaudenSummat, affectedAccounts)
    const t2370 = 0 - t3
    console.log('t20', t20, 't2370', t2370)

    // 478 = 470 + 495 + 496 + 497 + 474 - 475 + 476 - 477
    const v478 = this._asetaNumero('478', arvot, () => this._currencyService.roundHalfUp(v470 + v495 + v496 + v497 + v474 - v475 + v476 - v477, 2))

    const v177 = this._asetaArvo('177', arvot, () => '3')
    if (v177 === '1') {
      const v178 = this._asetaArvo('178', arvot, () => '2')
    }

    const v531 = this._asetaArvo('531', arvot)

    arvot.push({ key: '999', value: '1' })

    // const formattedDateAndTimeStr = this._dateService.muotoileVeroFormaattiPaivaJaAikaDate(this._dateService.timestampToDate(muodostettu))

    const arvot2: IlmoitusRivi[] = []
    arvot2.push({ key: '000', value: 'VSY06225' }) // File identifier
    arvot2.push({ key: '198', value: formattedDateAndTimeStr }) // Software-generated timestamp
    arvot2.push({ key: '048', value: 'Tilitoimisto Lemon Tree Oy' }) // ID of the software that produced the file
    arvot2.push({ key: '014', value: '2599105-8_LT' }) // ID of the software that produced the file (y-tunnus + 2 alphanums)
    arvot2.push({ key: '010', value: asiakas.ytunnus }) // Verovelvollisen y- tai henkilötunnus
    arvot2.push({ key: '054', value: tilikausiFormatted }) // 054 = tilikausi, jolta veroilmoitusta ollaan antamassa.

    const lomakeTunniste: VeroilmoitusLomakeTunniste = 'VSY062'

    const tunnistev871 = this.annaLomakeTunniste('871', lomakeTunniste)
    const tunnistev872 = this.annaLomakeTunniste('872', lomakeTunniste)
    const tunnistev873 = this.annaLomakeTunniste('873', lomakeTunniste)
    const tunnistev874 = this.annaLomakeTunniste('874', lomakeTunniste)
    const tunnistev877 = this.annaLomakeTunniste('877', lomakeTunniste)
    const tunnistev881 = this.annaLomakeTunniste('881', lomakeTunniste)
    const tunnistev865 = this.annaLomakeTunniste('865', lomakeTunniste)

    const tunnistev866 = this.annaLomakeTunniste('866', lomakeTunniste)
    const tunnistev867 = this.annaLomakeTunniste('867', lomakeTunniste)
    const tunnistev868 = this.annaLomakeTunniste('868', lomakeTunniste)
    const tunnistev869 = this.annaLomakeTunniste('869', lomakeTunniste)

    const tunnistev882 = this.annaLomakeTunniste('882', lomakeTunniste)
    const tunnistev883 = this.annaLomakeTunniste('883', lomakeTunniste)
    const tunnistev884 = this.annaLomakeTunniste('884', lomakeTunniste)
    const tunnistev885 = this.annaLomakeTunniste('885', lomakeTunniste)
    const tunnistev581 = this.annaLomakeTunniste('581', lomakeTunniste)
    const tunnistev886 = this.annaLomakeTunniste('886', lomakeTunniste)
    const tunnistev584 = this.annaLomakeTunniste('584', lomakeTunniste)

    const tunnistev800 = this.annaLomakeTunniste('800', lomakeTunniste)
    const tunnistev801 = this.annaLomakeTunniste('801', lomakeTunniste)
    const tunnistev802 = this.annaLomakeTunniste('802', lomakeTunniste)
    const tunnistev803 = this.annaLomakeTunniste('803', lomakeTunniste)
    const tunnistev804 = this.annaLomakeTunniste('804', lomakeTunniste)
    const tunnistev805 = this.annaLomakeTunniste('805', lomakeTunniste)
    const tunnistev806 = this.annaLomakeTunniste('806', lomakeTunniste)
    const tunnistev807 = this.annaLomakeTunniste('807', lomakeTunniste)
    const tunnistev847 = this.annaLomakeTunniste('847', lomakeTunniste)
    const tunnistev848 = this.annaLomakeTunniste('848', lomakeTunniste)
    const tunnistev849 = this.annaLomakeTunniste('849', lomakeTunniste)

    const tunnistev808 = this.annaLomakeTunniste('808', lomakeTunniste)
    const tunnistev809 = this.annaLomakeTunniste('809', lomakeTunniste)
    const tunnistev810 = this.annaLomakeTunniste('810', lomakeTunniste)
    const tunnistev811 = this.annaLomakeTunniste('811', lomakeTunniste)
    const tunnistev812 = this.annaLomakeTunniste('812', lomakeTunniste)
    const tunnistev813 = this.annaLomakeTunniste('813', lomakeTunniste)
    const tunnistev814 = this.annaLomakeTunniste('814', lomakeTunniste)
    const tunnistev815 = this.annaLomakeTunniste('815', lomakeTunniste)
    const tunnistev850 = this.annaLomakeTunniste('850', lomakeTunniste)
    const tunnistev851 = this.annaLomakeTunniste('851', lomakeTunniste)
    const tunnistev852 = this.annaLomakeTunniste('852', lomakeTunniste)

    const tunnistev816 = this.annaLomakeTunniste('816', lomakeTunniste)
    const tunnistev817 = this.annaLomakeTunniste('817', lomakeTunniste)
    const tunnistev818 = this.annaLomakeTunniste('818', lomakeTunniste)
    const tunnistev819 = this.annaLomakeTunniste('819', lomakeTunniste)
    const tunnistev820 = this.annaLomakeTunniste('820', lomakeTunniste)
    const tunnistev821 = this.annaLomakeTunniste('821', lomakeTunniste)
    const tunnistev822 = this.annaLomakeTunniste('822', lomakeTunniste)
    const tunnistev823 = this.annaLomakeTunniste('823', lomakeTunniste)
    const tunnistev853 = this.annaLomakeTunniste('853', lomakeTunniste)
    const tunnistev854 = this.annaLomakeTunniste('854', lomakeTunniste)
    const tunnistev855 = this.annaLomakeTunniste('855', lomakeTunniste)

    const tunnistev824 = this.annaLomakeTunniste('824', lomakeTunniste)
    const tunnistev825 = this.annaLomakeTunniste('825', lomakeTunniste)
    const tunnistev826 = this.annaLomakeTunniste('826', lomakeTunniste)
    const tunnistev827 = this.annaLomakeTunniste('827', lomakeTunniste)
    const tunnistev828 = this.annaLomakeTunniste('828', lomakeTunniste)
    const tunnistev829 = this.annaLomakeTunniste('829', lomakeTunniste)
    const tunnistev830 = this.annaLomakeTunniste('830', lomakeTunniste)
    const tunnistev831 = this.annaLomakeTunniste('831', lomakeTunniste)
    const tunnistev856 = this.annaLomakeTunniste('856', lomakeTunniste)
    const tunnistev857 = this.annaLomakeTunniste('857', lomakeTunniste)
    const tunnistev858 = this.annaLomakeTunniste('858', lomakeTunniste)

    const tunnistev832 = this.annaLomakeTunniste('832', lomakeTunniste)
    const tunnistev833 = this.annaLomakeTunniste('833', lomakeTunniste)
    const tunnistev834 = this.annaLomakeTunniste('834', lomakeTunniste)
    const tunnistev835 = this.annaLomakeTunniste('835', lomakeTunniste)
    const tunnistev836 = this.annaLomakeTunniste('836', lomakeTunniste)
    const tunnistev837 = this.annaLomakeTunniste('837', lomakeTunniste)
    const tunnistev838 = this.annaLomakeTunniste('838', lomakeTunniste)
    const tunnistev839 = this.annaLomakeTunniste('839', lomakeTunniste)
    const tunnistev859 = this.annaLomakeTunniste('859', lomakeTunniste)
    const tunnistev860 = this.annaLomakeTunniste('860', lomakeTunniste)
    const tunnistev861 = this.annaLomakeTunniste('861', lomakeTunniste)

    const tunnistev840 = this.annaLomakeTunniste('840', lomakeTunniste)
    const tunnistev841 = this.annaLomakeTunniste('841', lomakeTunniste)
    const tunnistev842 = this.annaLomakeTunniste('842', lomakeTunniste)
    const tunnistev844 = this.annaLomakeTunniste('844', lomakeTunniste)
    const tunnistev845 = this.annaLomakeTunniste('845', lomakeTunniste)
    const tunnistev862 = this.annaLomakeTunniste('862', lomakeTunniste)
    const tunnistev863 = this.annaLomakeTunniste('863', lomakeTunniste)
    const tunnistev864 = this.annaLomakeTunniste('864', lomakeTunniste)

    // 871 = vapaa kenttä
    const v871 = this._asetaNumeroLomake('871', tunnistev871, arvot2)
    // 872 = vapaa kenttä
    const v872 = this._asetaNumeroLomake('872', tunnistev872, arvot2)
    // 873 = vapaa kenttä
    const v873 = this._asetaNumeroLomake('873', tunnistev873, arvot2)
    // 874 = vapaa kenttä
    const v874 = this._asetaNumeroLomake('874', tunnistev874, arvot2)
    // 877 = vapaa kenttä
    const v877 = this._asetaNumeroLomake('877', tunnistev877, arvot2)
    // 881 = vapaa kenttä
    const v881 = this._asetaNumeroLomake('881', tunnistev881, arvot2)
    // 865 = vapaa kenttä
    const v865 = this._asetaNumeroLomake('865', tunnistev865, arvot2)

    // 866 = vapaa kenttä
    const v866 = this._asetaNumeroLomake('866', tunnistev866, arvot2)
    // 867 = vapaa kenttä
    const v867 = this._asetaNumeroLomake('867', tunnistev867, arvot2)
    // 868 = vapaa kenttä
    const v868 = this._asetaNumeroLomake('868', tunnistev868, arvot2)
    // 869 = vapaa kenttä
    const v869 = this._asetaNumeroLomake('869', tunnistev869, arvot2)

    // 882 = vapaa kenttä
    const v882 = this._asetaNumeroLomake('882', tunnistev882, arvot2)
    // 883 = vapaa kenttä
    const v883 = this._asetaNumeroLomake('883', tunnistev883, arvot2)
    // 884 = vapaa kenttä
    const v884 = this._asetaNumeroLomake('884', tunnistev884, arvot2, () => this._minus(v882, v883)) // 882 - 883 = 884
    // 885 = vapaa kenttä
    const v885 = this._asetaNumeroLomake('885', tunnistev885, arvot2)
    // 581 = vapaa kenttä
    const v581 = this._asetaNumeroLomake('581', tunnistev581, arvot2)
    // 886 = vapaa kenttä
    const v886 = this._asetaNumeroLomake('886', tunnistev886, arvot2)
    // 584 = vapaa kenttä
    const v584 = this._asetaNumeroLomake('584', tunnistev584, arvot2, () => this._sum(v885, v886)) // 885 + 886 = 584
    // 800 = vapaa kenttä(tilin 116 edellisen tilikauden loppusaldo)
    const v800 = this._asetaNumeroLomake('800', tunnistev800, arvot2, () => summatService.laskeDebetMinusKredit('116', edellisenTilikaudenSummat, affectedAccounts))

    // 801 = vapaa kenttä(tilin 116 debet yhteensä tilikauden aikana)
    const v801 = this._asetaNumeroLomake('801', tunnistev801, arvot2, () => {
      const v801EdellisenVuodenLoppusaldo = summatService.laskeDebet('116', edellisenTilikaudenSummat, affectedAccounts)
      const v801NykyisenVuodenLoppusaldo = summatService.laskeDebet('116', tilikaudenSummat, affectedAccounts)
      return v801NykyisenVuodenLoppusaldo - v801EdellisenVuodenLoppusaldo
    })

    // 803 = vapaa kenttä
    const v803 = this._asetaNumeroLomake('803', tunnistev803, arvot2)
    // 804 = vapaa kenttä(tili 68)
    const v804 = this._asetaNumeroLomake('804', tunnistev804, arvot2, () => t6800to6889)

    // 802 = vapaa kenttä(tilin 116 kredit yhteensä tilikauden aikana)
    const v802 = this._asetaNumeroLomake('802', tunnistev802, arvot2, () => {
      const v802EdellisenVuodenLoppusaldo = summatService.laskeKredit('116', edellisenTilikaudenSummat, affectedAccounts)
      const v802NykyisenVuodenLoppusaldo = summatService.laskeKredit('116', tilikaudenSummat, affectedAccounts)
      return v802NykyisenVuodenLoppusaldo - v802EdellisenVuodenLoppusaldo - (v804 ?? 0)
    })
    // 805 = vapaa kenttä(ei ehdotusta)
    const v805 = this._asetaNumeroLomake('805', tunnistev805, arvot2)
    // 806 = vapaa kenttä(tilin 116 tämän tilikauden loppusaldo)
    const v806 = this._asetaNumeroLomake('806', tunnistev806, arvot2, () => summatService.laskeDebetMinusKredit('116', tilikaudenSummat, affectedAccounts))
    // 807 = vapaa kenttä(tili 68)
    const v807 = this._asetaNumeroLomake('807', tunnistev807, arvot2, () => t6800to6889)
    // 847 = vapaa kenttä
    const v847 = this._asetaNumeroLomake('847', tunnistev847, arvot2)
    // 848 = vapaa kenttä
    const v848 = this._asetaNumeroLomake('848', tunnistev848, arvot2)
    // 849 = vapaa kenttä
    const v849 = this._asetaNumeroLomake('849', tunnistev849, arvot2)

    // 808 = vapaa kenttä
    const v808 = this._asetaNumeroLomake('808', tunnistev808, arvot2)
    // 809 = vapaa kenttä
    const v809 = this._asetaNumeroLomake('809', tunnistev809, arvot2)
    // 810 = vapaa kenttä
    const v810 = this._asetaNumeroLomake('810', tunnistev810, arvot2)
    // 811 = vapaa kenttä
    const v811 = this._asetaNumeroLomake('811', tunnistev811, arvot2)
    // 812 = vapaa kenttä
    const v812 = this._asetaNumeroLomake('812', tunnistev812, arvot2)
    // 813 = vapaa kenttä
    const v813 = this._asetaNumeroLomake('813', tunnistev813, arvot2)
    // 814 = vapaa kenttä
    const v814 = this._asetaNumeroLomake('814', tunnistev814, arvot2)
    // 815 = vapaa kenttä
    const v815 = this._asetaNumeroLomake('815', tunnistev815, arvot2)
    // 850 = vapaa kenttä
    const v850 = this._asetaNumeroLomake('850', tunnistev850, arvot2)
    // 851 = vapaa kenttä
    const v851 = this._asetaNumeroLomake('851', tunnistev851, arvot2)
    // 852 = vapaa kenttä
    const v852 = this._asetaNumeroLomake('852', tunnistev852, arvot2)

    // 816 = vapaa kenttä
    const v816 = this._asetaNumeroLomake('816', tunnistev816, arvot2)
    // 817 = vapaa kenttä
    const v817 = this._asetaNumeroLomake('817', tunnistev817, arvot2)
    // 818 = vapaa kenttä
    const v818 = this._asetaNumeroLomake('818', tunnistev818, arvot2)
    // 819 = vapaa kenttä
    const v819 = this._asetaNumeroLomake('819', tunnistev819, arvot2)
    // 820 = vapaa kenttä
    const v820 = this._asetaNumeroLomake('820', tunnistev820, arvot2)
    // 821 = vapaa kenttä
    const v821 = this._asetaNumeroLomake('821', tunnistev821, arvot2)
    // 822 = vapaa kenttä
    const v822 = this._asetaNumeroLomake('822', tunnistev822, arvot2)
    // 823 = vapaa kenttä
    const v823 = this._asetaNumeroLomake('823', tunnistev823, arvot2)
    // 853 = vapaa kenttä
    const v853 = this._asetaNumeroLomake('853', tunnistev853, arvot2)
    // 854 = vapaa kenttä
    const v854 = this._asetaNumeroLomake('854', tunnistev854, arvot2)
    // 855 = vapaa kenttä
    const v855 = this._asetaNumeroLomake('855', tunnistev855, arvot2)

    // 824 = vapaa kenttä
    const v824 = this._asetaNumeroLomake('824', tunnistev824, arvot2)
    // 825 = vapaa kenttä
    const v825 = this._asetaNumeroLomake('825', tunnistev825, arvot2)
    // 826 = vapaa kenttä
    const v826 = this._asetaNumeroLomake('826', tunnistev826, arvot2)
    // 827 = vapaa kenttä
    const v827 = this._asetaNumeroLomake('827', tunnistev827, arvot2)
    // 828 = vapaa kenttä
    const v828 = this._asetaNumeroLomake('828', tunnistev828, arvot2)
    // 829 = vapaa kenttä
    const v829 = this._asetaNumeroLomake('829', tunnistev829, arvot2)
    // 830 = vapaa kenttä
    const v830 = this._asetaNumeroLomake('830', tunnistev830, arvot2)
    // 831 = vapaa kenttä
    const v831 = this._asetaNumeroLomake('831', tunnistev831, arvot2)
    // 856 = vapaa kenttä
    const v856 = this._asetaNumeroLomake('856', tunnistev856, arvot2)
    // 857 = vapaa kenttä
    const v857 = this._asetaNumeroLomake('857', tunnistev857, arvot2)
    // 858 = vapaa kenttä
    const v858 = this._asetaNumeroLomake('858', tunnistev858, arvot2)

    // 832 = vapaa kenttä
    const v832 = this._asetaNumeroLomake('832', tunnistev832, arvot2)
    // 833 = vapaa kenttä
    const v833 = this._asetaNumeroLomake('833', tunnistev833, arvot2)
    // 834 = vapaa kenttä
    const v834 = this._asetaNumeroLomake('834', tunnistev834, arvot2)
    // 835 = vapaa kenttä
    const v835 = this._asetaNumeroLomake('835', tunnistev835, arvot2)
    // 836 = vapaa kenttä
    const v836 = this._asetaNumeroLomake('836', tunnistev836, arvot2)
    // 837 = vapaa kenttä
    const v837 = this._asetaNumeroLomake('837', tunnistev837, arvot2)
    // 838 = vapaa kenttä
    const v838 = this._asetaNumeroLomake('838', tunnistev838, arvot2)
    // 839 = vapaa kenttä
    const v839 = this._asetaNumeroLomake('839', tunnistev839, arvot2)
    // 859 = vapaa kenttä
    const v859 = this._asetaNumeroLomake('859', tunnistev859, arvot2)
    // 860 = vapaa kenttä
    const v860 = this._asetaNumeroLomake('860', tunnistev860, arvot2)
    // 861 = vapaa kenttä
    const v861 = this._asetaNumeroLomake('861', tunnistev861, arvot2)

    // 840 = vapaa kenttä
    const v840 = this._asetaNumeroLomake('840', tunnistev840, arvot2)
    // 841 = vapaa kenttä
    const v841 = this._asetaNumeroLomake('841', tunnistev841, arvot2)
    // 842 = vapaa kenttä
    const v842 = this._asetaNumeroLomake('842', tunnistev842, arvot2)
    // 844 = vapaa kenttä
    const v844 = this._asetaNumeroLomake('844', tunnistev844, arvot2)
    // 845 = vapaa kenttä
    const v845 = this._asetaNumeroLomake('845', tunnistev845, arvot2)
    // 862 = vapaa kenttä
    const v862 = this._asetaNumeroLomake('862', tunnistev862, arvot2)
    // 863 = vapaa kenttä
    const v863 = this._asetaNumeroLomake('863', tunnistev863, arvot2)
    // 864 = vapaa kenttä
    const v864 = this._asetaNumeroLomake('864', tunnistev864, arvot2)

    arvot2.push({ key: '999', value: '2' })

    this.tietokannastaLaskennoille = null
    this.eiTallennetutLaskennoille = null
    this.oletuksetLaskennoille = null

    const erottavatTilitFilterSet = summatService.annaTilinJaLastenNumerot('237')
    const erottavatTilit = summatService.annaTilitJoissaOnErottavaSumma(affectedAccounts, tilikaudenSummat, (tili): boolean => {
      return !erottavatTilitFilterSet.has(tili)
    })

    const ret: VeroilmoituksenPaivityksenTulos = {
      arvot: arvot.concat(arvot2),
      oletukset: oletukset,
      erottavatTilit: erottavatTilit,
      virheet: null,
    }

    const virheet: VeroilmoitusValidationError[] = this._validoi(arvot).concat(this._validoi62(arvot2))
    if (virheet.length) {
      ret.virheet = virheet
    }

    return ret
  }

  private _setOptionalLuku(numero: string, arvo: number, arvot: IlmoitusRivi[]) {
    if (arvo !== undefined && arvo !== null) {
      arvot.push({ key: numero, value: this._annaLuku(arvo) })
    }
  }

  private _validoi62(rivit: IlmoitusRivi[]): VeroilmoitusValidationError[] {
    const errors: VeroilmoitusValidationError[] = []

    const maps = this._mapValues(rivit)
    const numberMap = maps.numberMap
    const stringMap = maps.stringMap

    /**
     * #1811; Depreciation expense (under the act on business tax) (804)
     *   cannot be higher than 25% of the sum total of the following:
     *     Undepreciated balance at the start of the year (§30 and §31) (800) +
     *     Additions (801) –
     *     Selling prices and insurance indemnities (802) –
     *     Covered by replacement reserve (803).
     *
     *   No check process is undertaken if the following is max. €1,200.00 :
     *     Undepreciated balance at the start of the year (§30 and §31) (800) +
     *     Additions (801) –
     *     Selling prices and insurance indemnities (802) –
     *     Covered by replacement reserve (803).
     */
    const v804 = this._sumFromMap(numberMap, '804')
    const summa = this._currencyService.roundHalfUp(this._sumFromMap(numberMap, '800', '801') - this._sumFromMap(numberMap, '802', '803'), 2)
    const kentan804MaksimitulosValitulos = summa * 0.25
    const kentan804Maksimitulos = this._currencyService.roundHalfUp(kentan804MaksimitulosValitulos, 2)
    // console.log('v804', v804, 'kentan804Maksimitulos', kentan804Maksimitulos)
    if (summa > 1200 && v804 > kentan804Maksimitulos) {
      errors.push({
        fields: '804',
        message: `Säännönmukainen poisto (EVL) (804) saa olla enintään 25% seuraavan laskukaavan lopputuloksesta:
\tIrtaimen käyttöomaisuuden verotuksessa jäljellä oleva menojäännös verovuoden alussa(Evl 30 ja 31§) (800) +
\tLisäykset(801) -
\tLuovutushinnat ja vakuutuskorvaukset(802) -
\tKatettu jälleenhankintavarauksella(803)`,
      })
    }

    /*
        808
        809
        810
        811
        812
    #1272; Säännönmukainen poisto (7 %) (812) saa olla enintään 7 % seuraavan laskukaavan lopputuloksesta: Rakennusten poistamaton hankintameno vero - vuoden alussa(evl 34.2 § 1k)(808) + Lisäykset(809) - Luovutetun hyödykkeen verotuksessa poistamaton hankintameno(810) - Katettu jälleenhankintavarauk - sella(811)
    */
    const v812 = this._sumFromMap(numberMap, '812')
    const kentan812MaksimitulosValitulos = (this._sumFromMap(numberMap, '808', '809') - this._sumFromMap(numberMap, '810', '811')) * 0.07
    const kentan812Maksimitulos = this._currencyService.roundHalfUp(kentan812MaksimitulosValitulos, 2)
    // console.log('v812', v812, 'kentan812Maksimitulos', kentan812Maksimitulos)
    if (v812 > kentan812Maksimitulos) {
      errors.push({
        fields: '812',
        message: `Säännönmukainen poisto (7 %) (812) saa olla enintään 7 % seuraavan laskukaavan lopputuloksesta:
\tRakennusten poistamaton hankintameno verovuoden alussa (evl 34.2 § 1k) (808) +
\tLisäykset (809) -
\tLuovutetun hyödykkeen verotuksessa poistamaton hankintameno (810) -
\tKatettu jälleenhankintavarauksella (811)`,
      })
    }

    /*
        816
        817
        818
        819
        820
    #1273; Säännönmukainen poisto(4 %)(820) saa olla enintään 4 % seuraavan laskukaavan lopputuloksesta: Rakennusten poistamaton hankintameno vero - vuoden alussa(Evl 34.2 § 2k)(816) + Lisäykset(817) - Luovutetun hyödykkeen verotuksessa poistamaton hankintameno(818) - Katettu jälleenhankintavarauk - sella(819)
    */
    const v820 = this._sumFromMap(numberMap, '820')
    const kentan820MaksimitulosValitulos = (this._sumFromMap(numberMap, '816', '817') - this._sumFromMap(numberMap, '818', '819')) * 0.04
    const kentan820Maksimitulos = this._currencyService.roundHalfUp(kentan820MaksimitulosValitulos, 2)
    // console.log('v820', v820, 'kentan812Maksimitulos', kentan820Maksimitulos)
    if (v820 > kentan820Maksimitulos) {
      errors.push({
        fields: '820',
        message: `Säännönmukainen poisto (4 %) (820) saa olla enintään 4 % seuraavan laskukaavan lopputuloksesta:
\tRakennusten poistamaton hankintameno verovuoden alussa (Evl 34.2 § 2k) (816) +
\tLisäykset (817) -
\tLuovutetun hyödykkeen verotuksessa poistamaton hankintameno (818) -
\tKatettu jälleenhankintavarauksella (819)`,
      })
    }
    this._tarkistaR13Pilkku2('871', numberMap, errors)
    this._tarkistaR13Pilkku2('872', numberMap, errors)
    this._tarkistaR13Pilkku2('873', numberMap, errors)
    this._tarkistaR13Pilkku2('874', numberMap, errors)
    this._tarkistaR13Pilkku2('877', numberMap, errors)
    this._tarkistaR13Pilkku2('881', numberMap, errors)
    this._tarkistaR13Pilkku2('865', numberMap, errors)

    this._tarkistaR13Pilkku2('866', numberMap, errors)
    this._tarkistaR13Pilkku2('867', numberMap, errors)
    this._tarkistaR13Pilkku2('868', numberMap, errors)
    this._tarkistaR13Pilkku2('869', numberMap, errors)

    this._tarkistaR13Pilkku2('882', numberMap, errors)
    this._tarkistaR13Pilkku2('883', numberMap, errors)
    this._tarkistaR13Pilkku2('884', numberMap, errors)
    this._tarkistaR13Pilkku2('885', numberMap, errors)
    this._tarkistaR13Pilkku2('581', numberMap, errors)
    this._tarkistaR13Pilkku2('886', numberMap, errors)
    this._tarkistaR13Pilkku2('584', numberMap, errors)

    this._tarkistaR13Pilkku2('800', numberMap, errors)
    this._tarkistaR13Pilkku2('801', numberMap, errors)
    this._tarkistaR13Pilkku2('802', numberMap, errors)
    this._tarkistaR13Pilkku2('803', numberMap, errors)
    this._tarkistaR13Pilkku2('804', numberMap, errors)
    this._tarkistaR13Pilkku2('805', numberMap, errors)
    this._tarkistaR13Pilkku2('806', numberMap, errors)
    this._tarkistaR13Pilkku2('807', numberMap, errors)
    this._tarkistaR13Pilkku2('847', numberMap, errors)
    this._tarkistaR13Pilkku2('848', numberMap, errors)

    this._tarkistaR13Pilkku2('808', numberMap, errors)
    this._tarkistaR13Pilkku2('809', numberMap, errors)
    this._tarkistaR13Pilkku2('810', numberMap, errors)
    this._tarkistaR13Pilkku2('811', numberMap, errors)
    this._tarkistaR13Pilkku2('812', numberMap, errors)
    this._tarkistaR13Pilkku2('813', numberMap, errors)
    this._tarkistaR13Pilkku2('814', numberMap, errors)
    this._tarkistaR13Pilkku2('815', numberMap, errors)
    this._tarkistaR13Pilkku2('850', numberMap, errors)
    this._tarkistaR13Pilkku2('851', numberMap, errors)

    this._tarkistaR13Pilkku2('816', numberMap, errors)
    this._tarkistaR13Pilkku2('817', numberMap, errors)
    this._tarkistaR13Pilkku2('818', numberMap, errors)
    this._tarkistaR13Pilkku2('819', numberMap, errors)
    this._tarkistaR13Pilkku2('820', numberMap, errors)
    this._tarkistaR13Pilkku2('821', numberMap, errors)
    this._tarkistaR13Pilkku2('822', numberMap, errors)
    this._tarkistaR13Pilkku2('823', numberMap, errors)
    this._tarkistaR13Pilkku2('853', numberMap, errors)
    this._tarkistaR13Pilkku2('854', numberMap, errors)

    this._tarkistaR13Pilkku2('824', numberMap, errors)
    this._tarkistaR13Pilkku2('825', numberMap, errors)
    this._tarkistaR13Pilkku2('826', numberMap, errors)
    this._tarkistaR13Pilkku2('827', numberMap, errors)
    this._tarkistaR13Pilkku2('828', numberMap, errors)
    this._tarkistaR13Pilkku2('829', numberMap, errors)
    this._tarkistaR13Pilkku2('830', numberMap, errors)
    this._tarkistaR13Pilkku2('831', numberMap, errors)
    this._tarkistaR13Pilkku2('856', numberMap, errors)
    this._tarkistaR13Pilkku2('857', numberMap, errors)

    this._tarkistaR13Pilkku2('832', numberMap, errors)
    this._tarkistaR13Pilkku2('833', numberMap, errors)
    this._tarkistaR13Pilkku2('834', numberMap, errors)
    this._tarkistaR13Pilkku2('835', numberMap, errors)
    this._tarkistaR13Pilkku2('836', numberMap, errors)
    this._tarkistaR13Pilkku2('837', numberMap, errors)
    this._tarkistaR13Pilkku2('838', numberMap, errors)
    this._tarkistaR13Pilkku2('839', numberMap, errors)
    this._tarkistaR13Pilkku2('859', numberMap, errors)
    this._tarkistaR13Pilkku2('860', numberMap, errors)

    this._tarkistaR13Pilkku2('840', numberMap, errors)
    this._tarkistaR13Pilkku2('841', numberMap, errors)
    this._tarkistaR13Pilkku2('842', numberMap, errors)
    this._tarkistaR13Pilkku2('844', numberMap, errors)
    this._tarkistaR13Pilkku2('845', numberMap, errors)
    this._tarkistaR13Pilkku2('862', numberMap, errors)
    this._tarkistaR13Pilkku2('863', numberMap, errors)

    this._tarkistaG13Pilkku2('849', numberMap, errors)
    this._tarkistaG13Pilkku2('852', numberMap, errors)
    this._tarkistaG13Pilkku2('855', numberMap, errors)
    this._tarkistaG13Pilkku2('858', numberMap, errors)
    this._tarkistaG13Pilkku2('861', numberMap, errors)
    this._tarkistaG13Pilkku2('864', numberMap, errors)

    return errors
  }

  private _validoi(rivit: IlmoitusRivi[]): VeroilmoitusValidationError[] {
    const errors: VeroilmoitusValidationError[] = []
    const maps = this._mapValues(rivit)
    const numberMap = maps.numberMap
    const stringMap = maps.stringMap

    const osakkaat = this.groupIlmoitusrivitByOsakkaat(rivit)
    const huoneistot = this.groupIlmoitusrivitByOsakkaidenHuoneistot(rivit)
    const osingot = this.groupIlmoitusrivitByOsingot(rivit)
    const vapaastaOmastaPaaomastaJaetut = this.groupIlmoitusrivitByVapaastaPaaomastaJaettujen(rivit)

    //     531 #1888; Verovuosi, jolloin vaihto on tapahtunut(vvvv)(531) on oltava väliltä 2015 - 2025
    const vaihtoverovuosi = numberMap.get('531')
    if (vaihtoverovuosi && (vaihtoverovuosi < 2015 || vaihtoverovuosi > 2025)) {
      errors.push({
        fields: '531',
        message: 'Verovuosi, jolloin vaihto on tapahtunut (vvvv) (531) on oltava väliltä 2015 - 2025',
      })
    }

    //     565 580
    // #1209; Jos tieto Korotus: maksullinen = 2, maksuton = 3(565) on ilmoitettu, myös tieto Osakkeiden lukumäärä muutoksen jälkeen(580) pitää antaa.
    // *
    //       Merkitse verovuosi, jolloin vaihto on tapahtunut, jos yli puolet osakkeista tai osuuksista on vaihtanut omistajaa verovuoden aikana. Jos vaihtuminen on tapahtunut vaiheittain usean vuoden aikana, merkitse se verovuosi, jota ennen tai jonka aikana syntyneet tappiot eivät omistajanvaihdosten vuoksi ole vähennyskelpoisia
    const v565 = stringMap.get('565')
    const v580 = numberMap.get('580')
    if (v565 === '2' || v565 === '3') {
      if (v580 === undefined || v580 === null) {
        errors.push({
          fields: '565, 580',
          message:
            'Merkitse verovuosi, jolloin vaihto on tapahtunut, jos yli puolet osakkeista tai osuuksista on vaihtanut omistajaa verovuoden aikana. Jos vaihtuminen on tapahtunut vaiheittain usean vuoden aikana, merkitse se verovuosi, jota ennen tai jonka aikana syntyneet tappiot eivät omistajanvaihdosten vuoksi ole vähennyskelpoisia',
        })
      }
    }

    //     573#574
    //     500#505
    //     474#475
    //     476#477
    //     573 tai 574
    //     500 tai 505 (Tätä tarkistusta ei tehdä, jos kentässä 697 on annettu tietoa)
    //     474 tai 475
    //     476 tai 477
    //     #94; Kentän # poissulkeva kenttä & sisältää jo arvon. Vain jommankumman arvoista voi ilmoittaa
    //     500 - 505 573 - 574
    this.validateVainJompikumpiKentista('573', '574', errors, numberMap)
    const v697 = numberMap.get('697')
    if (v697 === undefined || (v697 === null && v697 === 0)) {
      this.validateVainJompikumpiKentista('500', '505', errors, numberMap)
    }
    this.validateVainJompikumpiKentista('474', '475', errors, numberMap)
    this.validateVainJompikumpiKentista('476', '477', errors, numberMap)

    // #1210; Joko 500 tai 505 pitää antaa. Toisen tai molemmat voi antaa myös nollana.
    this.validateJompikumpiIlmoitettuTaiAinakinToinenNolla('500', '505', errors, numberMap)

    // #1211; Joko 573 tai 574 pitää antaa. Toisen tai molemmat voi antaa myös nollana.
    this.validateJompikumpiIlmoitettuTaiAinakinToinenNolla('573', '574', errors, numberMap)

    for (const osinko of osingot) {
      const v537 = this._etsiArvo('537', osinko) || null
      const v538 = this._etsiArvo('538', osinko) || null

      // #380; Kun "Jaettavaksi päätetty osinko" (522) on suurempi kuin nolla, niin "Osingonjakopäätös tehty ppkkvvvv" (537) ja Osinko nostettavissa pvm(538) pitää ilmoittaa.
      const v522 = this._etsiNumero('522', osinko) ?? 0
      if (v522 > 0) {
        if (!v537 || !v538) {
          errors.push({
            fields: '537, 538',
            message: 'Kun "Jaettavaksi päätetty osinko" (522) on suurempi kuin nolla, niin "Osingonjakopäätös tehty ppkkvvvv" (537) ja Osinko nostettavissa pvm (538) pitää ilmoittaa.',
          })
        }
      }

      if (v537) {
        // #1890 Kentän 537(osingonjakopäätöksen pvm) vuoden oltava verovuonna 2025 väliltä 2025 - 2027.
        const osingonjakoPaatoksenPvm = this._dateService.parsiVeroFormaattiPaiva(v537)
        if (osingonjakoPaatoksenPvm.year < 2025 || osingonjakoPaatoksenPvm.year > 2027) {
          errors.push({
            fields: '537',
            message: 'Osingonjakopäätöspäivämäärän vuoden on oltava väliltä 2025 - 2027',
          })
        }

        // #1386; Kun "Osingonjakopäätös tehty ppkkvvvv" (537) on ilmoitettu, niin "Jaettavaksi päätetty osinko" (522) pitää olla suurempi kuin nolla ja Osinko nostettavissa pvm (538) pitää antaa.
        if (v522 < 0.01 || !v538) {
          errors.push({
            fields: '537, 538',
            message:
              'Kun "Osingonjakopäätös tehty ppkkvvvv" (537) on ilmoitettu, niin "Jaettavaksi päätetty osinko" (522) pitää olla suurempi kuin nolla ja Osinko nostettavissa pvm (538) pitää antaa.',
          })
        }
      }

      if (v538) {
        // #1892 Kentän 538 (osinko nostettavissa pvm) vuoden oltava verovuonna 2025 väliltä 2025 - 2030
        const osinkoNostettavissaPvm = this._dateService.parsiVeroFormaattiPaiva(v538)
        if (osinkoNostettavissaPvm.year < 2025 || osinkoNostettavissaPvm.year > 2030) {
          errors.push({
            fields: '538',
            message: '"Osinko nostettavissa" päivämäärän vuoden on oltava väliltä 2025 - 2030',
          })
        }
      }
    }

    for (const vapaastaOmastaPaaomastaJaettu of vapaastaOmastaPaaomastaJaetut) {
      const v294 = this._etsiArvo('294', vapaastaOmastaPaaomastaJaettu) || null
      const v295 = this._etsiArvo('295', vapaastaOmastaPaaomastaJaettu) || null
      const v293 = this._etsiNumero('293', vapaastaOmastaPaaomastaJaettu) ?? 0

      if (v294) {
        // #1980; Kentän 294 (Vapaan oman pääoman rahastosta tehty varojen jakopäätöksen päivämäärä) vuoden oltava verovuonna 2025 väliltä 2025 - 2027.
        const vopJakoPaatoksenPvm = this._dateService.parsiVeroFormaattiPaiva(v294)
        if (vopJakoPaatoksenPvm.year < 2025 || vopJakoPaatoksenPvm.year > 2027) {
          errors.push({
            fields: '294',
            message: 'Kentän 294 (Vapaan oman pääoman rahastosta tehty varojen jakopäätöksen päivämäärä) vuoden oltava verovuonna 2025 väliltä 2025 - 2027.',
          })
        }

        // #1389; Jos "Vapaan oman pääoman rahastosta tehty varojen jakopäätöksen päivämäärä" (294) on ilmoitettu, pitää "Vapaan oman pääoman rahastosta jaettavaksi päätetty määrä" (293) olla suurempi kuin nolla ja Vapaan oman pääoman rahastosta tehty varojen jako nostettavissa, päivämäärä (295) ilmoittaa. "Vapaan oman pääoman rahastosta jaettavaksi päätetty määrä" (293) ei voi olla nolla.
        if (v293 < 0.01 || !v295) {
          errors.push({
            fields: '293, 295',
            message:
              'Jos "Vapaan oman pääoman rahastosta tehty varojen jakopäätöksen päivämäärä" (294) on ilmoitettu, pitää "Vapaan oman pääoman rahastosta jaettavaksi päätetty määrä" (293) olla suurempi kuin nolla ja Vapaan oman pääoman rahastosta tehty varojen jako nostettavissa, päivämäärä (295) ilmoittaa. "Vapaan oman pääoman rahastosta jaettavaksi päätetty määrä" (293) ei voi olla nolla.',
          })
        }
      }

      if (v295) {
        // #1978; Kentän 295 (Vapaan oman pääoman rahastosta tehty varojen jako nostettavissa, päivämäärä) vuoden oltava verovuonna 2025 väliltä 2025 - 2030.
        const vopJakoPaatoksenPvm = this._dateService.parsiVeroFormaattiPaiva(v295)
        if (vopJakoPaatoksenPvm.year < 2025 || vopJakoPaatoksenPvm.year > 2030) {
          errors.push({
            fields: '295',
            message: 'Kentän 295 (Vapaan oman pääoman rahastosta tehty varojen jako nostettavissa, päivämäärä) vuoden oltava verovuonna 2025 väliltä 2025 - 2030.',
          })
        }
      }

      // #1388; Jos "Vapaan oman pääoman rahastosta jaettavaksi päätetty määrä" (293) on suurempi kuin nolla, pitää "Vapaan oman pääoman rahastosta tehty varojen jakopäätöksen päivämäärä" (294) ja Vapaan oman pääoman rahastosta tehty varojen jako nostettavissa, päivämäärä (295) ilmoittaa.
      if (v293 > 0) {
        if (!v294 || !v295) {
          errors.push({
            fields: '294, 295',
            message:
              'Jos "Vapaan oman pääoman rahastosta jaettavaksi päätetty määrä" (293) on suurempi kuin nolla, pitää "Vapaan oman pääoman rahastosta tehty varojen jakopäätöksen päivämäärä" (294) ja Vapaan oman pääoman rahastosta tehty varojen jako nostettavissa, päivämäärä (295) ilmoittaa.',
          })
        }
      }
      if (!v293 || !v294 || !v295) {
        errors.push({
          fields: '293, 294, 295',
          message: 'Vapaan oman pääoman rahastosta jaettavat varat: Täytä kentät tai poista tyhjä osio.',
        })
      }
    }

    let ainakinYksiAsetettu = false
    for (const osakas of osakkaat) {
      const v701 = this._etsiArvo('701', osakas)
      const v703 = this._etsiArvo('703', osakas)
      const v570 = this._etsiKokonaisluku('570', osakas)

      if (v703) {
        this._tarkistaHetuTaiYTunnus('703', v703, errors)
      }

      if (v701 && v703 && (v570 || v570 === 0)) {
        ainakinYksiAsetettu = true
      } else {
        // #330; Jos on annettu jokin kohdista "Osakkeiden lukumäärä" (570), "Osakkeenomistajan nimi" (701) tai "Henkilö- tai Y-tunnus" (703), täytyy ilmoittaa kaikki kolme tietoa. Tiedot voivat olla myös nollaa.
        errors.push({
          fields: '570, 701, 703',
          message:
            'Jos on annettu jokin kohdista "Osakkeiden lukumäärä" (570), "Osakkeenomistajan nimi" (701) tai "Henkilö- tai Y-tunnus" (703), täytyy ilmoittaa kaikki kolme tietoa. Tiedot voivat olla myös nollaa.',
        })
      }

      // #846; Jos "Vuokrat" (706) tai "Luovutukset" (707) tai "Korot" (708) tai "Muut suoritukset" (709) tai "Osakaslainat" (864) on suurempi kuin nolla pitää antaa "Osakkeenomistajan nimi" (701) ja "Henkilö- tai Y-tunnus" (703) ja "Osakkeiden lukumäärä" (570). "Osakkeiden lukumäärä" (570) voi olla nolla.
      if (this._etsiNumero('706', rivit) || this._etsiNumero('707', rivit) || this._etsiNumero('708', rivit) || this._etsiNumero('709', rivit) || this._etsiNumero('864', rivit)) {
        if (!v701 || !v703 || v570 === null || v570 === undefined) {
          errors.push({
            fields: '570, 701, 703',
            message:
              'Jos "Vuokrat" (706) tai "Luovutukset" (707) tai "Korot" (708) tai "Muut suoritukset" (709) tai "Osakaslainat" (864) on suurempi kuin nolla pitää antaa "Osakkeenomistajan nimi" (701) ja "Henkilö - tai Y - tunnus" (703) ja "Osakkeiden lukumäärä" (570)."Osakkeiden lukumäärä" (570) voi olla nolla.',
          })
        }
      }
    }

    const v185 = stringMap.get('185')
    if (!v185 && !ainakinYksiAsetettu) {
      // #517; Jos kohtaa "Kaikkien osakkaiden omistusosuus alle 10 %" (185) ei ole ilmoitettu, on annettava ainakin yhden osakkaan(tunnukset 701, 703 ja 570) tiedot.
      errors.push({
        fields: '185',
        message: 'Jos kohtaa "Kaikkien osakkaiden omistusosuus alle 10 %" (185) ei ole ilmoitettu, on annettava ainakin yhden osakkaan(tunnukset 701, 703 ja 570) tiedot.',
      })
    }

    const v177 = stringMap.get('177')
    const v178 = stringMap.get('178')

    // #442; Jos tilintarkastus on suoritettu(177 = 1), pitää myös kertoa sisältääkö se kielteisiä lausuntoja vai ei(178).
    if (v177 === '1' && !v178) {
      errors.push({
        fields: '177, 178',
        message: 'Jos tilintarkastus on suoritettu(177 = 1), pitää myös kertoa sisältääkö se kielteisiä lausuntoja vai ei (178).',
      })
    }

    // #471; Jos tilintarkastusta ei ole suoritettu(177 = 2 tai 3), ei saa antaa tietoa, sisältääkö tilintarkastuskertomus kielteisiä lausuntoja (178).
    if (v177 !== '1' && v178) {
      errors.push({
        fields: '177, 178',
        message: 'Jos tilintarkastusta ei ole suoritettu(177 = 2 tai 3), ei saa antaa tietoa, sisältääkö tilintarkastuskertomus kielteisiä lausuntoja (178).',
      })
    }

    // 583 #889; Jos on ilmoitettu tieto Uuden osakkeen merkintähinta (583) nollasta poikkeavalla arvolla, on ilmoitettava myös Merkintähintaa käytettävä uuden osakkeen matemaattisena arvona (1=kyllä, 0=ei) (585).
    const v583 = numberMap.get('583') || 0
    const v585 = stringMap.get('585')
    if (v583 && v585 !== '0' && v585 !== '1') {
      errors.push({
        fields: '538, 585',
        message:
          'Jos on ilmoitettu tieto Uuden osakkeen merkintähinta (583) nollasta poikkeavalla arvolla, on ilmoitettava myös Merkintähintaa käytettävä uuden osakkeen matemaattisena arvona (1=kyllä, 0=ei) (585).',
      })
    }

    for (const huoneisto of huoneistot) {
      const v722 = this._etsiArvo('722', huoneisto)
      const v724 = this._etsiArvo('724', huoneisto)
      const v729 = this._etsiArvo('729', huoneisto)
      const v730 = this._etsiNumero('730', huoneisto)

      // #333; Jos on annettu jokin tiedoista "Osakkaan henkilötunnus" (722), "Kiinteistön tai kiinteistö-/asunto-osakeyhtiön nimi" (724), "Käyttöaika asuntona ppkkvvvv – ppkkvvvv" (729) tai "Nettovarallisuuslaskelmassa käytetty arvo" (730), täytyy ilmoittaa kaikki tiedot. "Nettovarallisuuslaskelmassa käytetty arvo" (730) voi olla nolla.
      if (!v722 || !v724 || !v729 || v730 === null || v730 === undefined) {
        errors.push({
          fields: '722, 724, 729, 730',
          message:
            'Jos on annettu jokin tiedoista "Osakkaan henkilötunnus" (722), "Kiinteistön tai kiinteistö-/asunto-osakeyhtiön nimi" (724), "Käyttöaika asuntona ppkkvvvv - ppkkvvvv" (729) tai "Nettovarallisuuslaskelmassa käytetty arvo" (730), täytyy ilmoittaa kaikki tiedot. "Nettovarallisuuslaskelmassa käytetty arvo" (730) voi olla nolla.',
        })
      }
    }

    // OLD                                    (330 +  331 +  333 +  260 +  334 +  335 +  336 +  338 +  339 +  340 +  341 +  343 +  344 +  563 +  345 +  347 +  439 +  351 +  352 +  261 +  353)
    // NEW                                    (330 +  331 +  333 +  260 +  334 +  335 +  336 +  338 +  339 +  340 +  341 +  343 +  344 +  563 +  345 +  347 +  439 +  351 +  352 +  261 +  353)
    const tulot = this._sumFromMap(numberMap, '330', '331', '333', '260', '334', '335', '336', '338', '339', '340', '341', '343', '344', '563', '345', '347', '439', '351', '352', '261', '353')

    // OLD                                    (359 +  360 +  361 +  362 +  363 +  365 +  367 +  369 +  371 +  373 +  374 +  375 +  376 +  377 +  328 +  386 +  387 +  398 +  399 +  390 +  391 +  392 +  327 +  436 +  395 +  271 +  273 +  396)
    // NEW                                    (359 +  360 +  361 +  362 +  363 +  365 +  369 +  371 +  373 +  374 +  375 +  376 +  377 +  328 +  386 +  387 +  398 +  399 +  390 +  391 +  392 +  327 +  436 +  395 +  271 +  273 +  695 +  697 +  698 +  696 +  396)
    const menot = this._sumFromMap(
      numberMap,
      '359',
      '360',
      '361',
      '362',
      '363',
      '365',
      '369',
      '371',
      '373',
      '374',
      '375',
      '376',
      '377',
      '328',
      '386',
      '387',
      '398',
      '399',
      '390',
      '391',
      '392',
      '327',
      '436',
      '395',
      '271',
      '273',
      '695',
      '697',
      '698',
      '696',
      '396',
    )
    const tulos = this._currencyService.roundHalfUp(tulot - menot, 2)

    // console.log('500', this._etsiNumero('500', rivit))
    if (tulos > 0) {
      // 500 === kaava
      // Laskutoimituksen tuloksen ollessa positiivinen, merkitään luku tunnukselle 500.
      if (this._etsiNumero('500', rivit) !== tulos) {
        errors.push({
          fields: '500',
          message: 'Tulos ei täsmää summauksen kanssa. Ota yhteys ylläpitoon. (' + this._etsiNumero('500', rivit) + ' !== ' + tulos + ')',
        })
      }
    } else if (tulos < 0) {
      // JOS 505 annettu, (0 - 505) - 504 === kaava
      // Laskutoimituksen tuloksen ollessa negatiivinen, pitää luku antaa ilman miinusmerkkiä ja sen pitää olla tunnuksilla 504 ja 505 annettujen tietojen yhteissumma.
      if (0 - this._etsiNumero('505', rivit) - this._etsiNumero('504', rivit) !== tulos) {
        errors.push({
          fields: '505',
          message: 'Tulos ei täsmää summauksen kanssa. Ota yhteys ylläpitoon. (0 - ' + this._etsiNumero('505', rivit) + ' - ' + this._etsiNumero('504', rivit) + ' !== ' + tulos + ')',
        })
      }
    }

    // Laskutoimituksen tuloksen ollessa positiivinen, tulee summa tunnukselle 573. Laskutoimituksen tuloksen ollessa negatiivinen, tulee summa tunnukselle 574.
    // (400 + 401 + 402 + 403 + 404 + 405 + 406 + 407 + 408 + 409 + 411 + 412 + 413 + 414 + 415 + 416 + 417 + 419 + 420 + 421 + 422 + 423 + 424 + 425 + 463 + 426 + 427 + 428 + 430 + 431 + 432 + 437 + 438 + 433) - (450 + 451 + 452 + 453 + 454 + 455 + 456 + 457 + 464 + 465 + 458 + 459)
    const summa1 = this._sumFromMap(
      numberMap,
      '400',
      '401',
      '402',
      '403',
      '404',
      '405',
      '406',
      '407',
      '408',
      '409',
      '411',
      '412',
      '413',
      '414',
      '415',
      '416',
      '417',
      '419',
      '420',
      '421',
      '422',
      '423',
      '424',
      '425',
      '463',
      '426',
      '427',
      '428',
      '430',
      '431',
      '432',
      '437',
      '438',
      '433',
    )
    const summa2 = this._sumFromMap(numberMap, '450', '451', '452', '453', '454', '455', '456', '457', '464', '465', '458', '459')
    const erotus = this._currencyService.roundHalfUp(summa1 - summa2, 2)
    if (erotus > 0) {
      if (this._etsiNumero('573', rivit) !== erotus) {
        errors.push({
          fields: '573',
          message: 'Tulos ei täsmää summauksen kanssa. Ota yhteys ylläpitoon. (' + this._etsiNumero('573', rivit) + ' !== ' + erotus + ')',
        })
      }
    } else if (erotus < 0) {
      if (0 - this._etsiNumero('574', rivit) !== erotus) {
        errors.push({
          fields: '574',
          message: 'Tulos ei täsmää summauksen kanssa. Ota yhteys ylläpitoon. (0 - ' + this._etsiNumero('574', rivit) + ' !== ' + erotus + ')',
        })
      }
    }

    // ELINKEINOTOIMINNAN VERONALAISET TUOTOT YHTEENSÄ
    // 354 = 330 + 331 + 333 + 260 + 334 + 335 + 336 + 338 + 339 + 340 + 341 + 343 + 344 + 563 + 345 + 347 + 439 + 351 + 352 + 261 + 353
    const veronalaisetTuotot = this._sumFromMap(
      numberMap,
      '330',
      '331',
      '333',
      '260',
      '334',
      '335',
      '336',
      '338',
      '339',
      '340',
      '341',
      '343',
      '344',
      '563',
      '345',
      '347',
      '439',
      '351',
      '352',
      '261',
      '353',
    )
    if (this._etsiNumero('354', rivit) !== veronalaisetTuotot) {
      errors.push({
        fields: '354',
        message: 'Tulos ei täsmää summauksen kanssa. Ota yhteys ylläpitoon. (0 - ' + this._etsiNumero('354', rivit) + ' !== ' + veronalaisetTuotot + ')',
      })
    }

    // ELINKEINOTOIMINNAN VEROTUKSESSA VÄHENNYSKELPOISET KULUT YHTEENSÄ
    // OLD                                           397 = 359 +  360 +  361 +  362 +  363 +  365 +  367 +  369 +  371 +  373 +  374 +  375 +  376 +  377 +  328 +  386 +  387 +  398 +  399 +  390 +  391 +  392 +  327 +  436 +  395 +  271 +  273 +  695 +  697 +  698 +  696 +  396
    // NEW                                                 359 +  360 +  361 +  362 +  363 +  365 +  369 +  371 +  373 +  374 +  375 +  376 +  377 +  328 +  386 +  387 +  398 +  399 +  390 +  391 +  392 +  327 +  436 +  395 +  271 +  273 +  695 +  697 +  698 +  696 +  396.
    const vahennyskelpoiset = this._sumFromMap(
      numberMap,
      '359',
      '360',
      '361',
      '362',
      '363',
      '365',
      '369',
      '371',
      '373',
      '374',
      '375',
      '376',
      '377',
      '328',
      '386',
      '387',
      '398',
      '399',
      '390',
      '391',
      '392',
      '327',
      '436',
      '395',
      '271',
      '273',
      '695',
      '697',
      '698',
      '696',
      '396',
    )
    if (this._etsiNumero('397', rivit) !== vahennyskelpoiset) {
      errors.push({
        fields: '397',
        message: 'Tulos ei täsmää summauksen kanssa. Ota yhteys ylläpitoon. (0 - ' + this._etsiNumero('397', rivit) + ' !== ' + vahennyskelpoiset + ')',
      })
    }

    // VARAT YHTEENSÄ
    // 435 = 410 + 418 + 429 + 434
    const varat = this._sumFromMap(numberMap, '410', '418', '429', '434')
    if (this._etsiNumero('435', rivit) !== varat) {
      errors.push({
        fields: '435',
        message: 'Tulos ei täsmää summauksen kanssa. Ota yhteys ylläpitoon. (0 - ' + this._etsiNumero('435', rivit) + ' !== ' + varat + ')',
      })
    }

    // VELAT YHTEENSÄ
    // 460 = 450 + 451 + 452 + 453 + 454 + 455 + 456 + 457 + 464 + 465 + 458 + 459
    const velat = this._sumFromMap(numberMap, '450', '451', '452', '453', '454', '455', '456', '457', '464', '465', '458', '459')
    if (this._etsiNumero('460', rivit) !== velat) {
      errors.push({
        fields: '460',
        message: 'Tulos ei täsmää summauksen kanssa. Ota yhteys ylläpitoon. (0 - ' + this._etsiNumero('460', rivit) + ' !== ' + velat + ')',
      })
    }

    // OMA PÄÄOMA YHTEENSÄ
    // 478 = 470 + 495 + 496 + 497 + 474 - 475 + 476 - 477
    const paaoma = this._sumFromMapDebug(numberMap, '470', '495', '496', '497', '474', '476')
    const v475 = numberMap.get('475') || 0
    const v477 = numberMap.get('477') || 0
    console.log('475', v475)
    console.log('477', v477)
    const paaoma1 = this._currencyService.roundHalfUp(paaoma - v475 - v477, 2)
    console.log('Laskettu pääoma: ', paaoma1)
    console.log('Tileistä summattu pääoma: ', this._etsiNumero('478', rivit))
    if (this._etsiNumero('478', rivit) !== paaoma1) {
      errors.push({
        fields: '478',
        message: 'Tulos ei täsmää summauksen kanssa. Ota yhteys ylläpitoon. (0 - ' + this._etsiNumero('478', rivit) + ' !== ' + paaoma1 + ')',
      })
    }

    this._tarkistaPlusN15('570', numberMap, errors)
    this._tarkistaR13Pilkku2('706', numberMap, errors)
    this._tarkistaR13Pilkku2('708', numberMap, errors)
    this._tarkistaR13Pilkku2('707', numberMap, errors)
    this._tarkistaR13Pilkku2('709', numberMap, errors)
    this._tarkistaR13Pilkku2('864', numberMap, errors)

    this._tarkistaR13Pilkku2('730', numberMap, errors)
    this._tarkistaR13Pilkku2('522', numberMap, errors)
    this._tarkistaR13Pilkku2('293', numberMap, errors)
    this._tarkistaR13Pilkku2('591', numberMap, errors)
    this._tarkistaR13Pilkku2('592', numberMap, errors)
    this._tarkistaPlusN15('580', numberMap, errors)
    this._tarkistaR13Pilkku2('582', numberMap, errors)
    this._tarkistaR13Pilkku2('583', numberMap, errors)
    this._tarkistaPlusN15('571', numberMap, errors)

    this._tarkistaR13Pilkku2('500', numberMap, errors)
    this._tarkistaR13Pilkku2('504', numberMap, errors)
    this._tarkistaR13Pilkku2('505', numberMap, errors)

    this._tarkistaR13Pilkku2('400', numberMap, errors)
    this._tarkistaR13Pilkku2('401', numberMap, errors)
    this._tarkistaR13Pilkku2('402', numberMap, errors)
    this._tarkistaR13Pilkku2('403', numberMap, errors)
    this._tarkistaR13Pilkku2('404', numberMap, errors)
    this._tarkistaR13Pilkku2('405', numberMap, errors)
    this._tarkistaR13Pilkku2('406', numberMap, errors)
    this._tarkistaR13Pilkku2('407', numberMap, errors)
    this._tarkistaR13Pilkku2('408', numberMap, errors)
    this._tarkistaR13Pilkku2('409', numberMap, errors)
    this._tarkistaR13Pilkku2('410', numberMap, errors)

    this._tarkistaR13Pilkku2('411', numberMap, errors)
    this._tarkistaR13Pilkku2('412', numberMap, errors)
    this._tarkistaR13Pilkku2('413', numberMap, errors)
    this._tarkistaR13Pilkku2('414', numberMap, errors)
    this._tarkistaR13Pilkku2('415', numberMap, errors)
    this._tarkistaR13Pilkku2('416', numberMap, errors)
    this._tarkistaR13Pilkku2('417', numberMap, errors)
    this._tarkistaR13Pilkku2('418', numberMap, errors)
    this._tarkistaR13Pilkku2('419', numberMap, errors)
    this._tarkistaR13Pilkku2('420', numberMap, errors)
    this._tarkistaR13Pilkku2('421', numberMap, errors)
    this._tarkistaR13Pilkku2('422', numberMap, errors)
    this._tarkistaR13Pilkku2('423', numberMap, errors)
    this._tarkistaR13Pilkku2('424', numberMap, errors)
    this._tarkistaR13Pilkku2('425', numberMap, errors)
    this._tarkistaR13Pilkku2('463', numberMap, errors)
    this._tarkistaR13Pilkku2('426', numberMap, errors)
    this._tarkistaR13Pilkku2('427', numberMap, errors)
    this._tarkistaR13Pilkku2('428', numberMap, errors)
    this._tarkistaR13Pilkku2('429', numberMap, errors)

    this._tarkistaR13Pilkku2('430', numberMap, errors)
    this._tarkistaR13Pilkku2('431', numberMap, errors)
    this._tarkistaR13Pilkku2('432', numberMap, errors)
    this._tarkistaR13Pilkku2('437', numberMap, errors)
    this._tarkistaR13Pilkku2('438', numberMap, errors)
    this._tarkistaR13Pilkku2('433', numberMap, errors)
    this._tarkistaR13Pilkku2('434', numberMap, errors)
    this._tarkistaR13Pilkku2('435', numberMap, errors)

    this._tarkistaR13Pilkku2('450', numberMap, errors)
    this._tarkistaR13Pilkku2('451', numberMap, errors)
    this._tarkistaR13Pilkku2('452', numberMap, errors)
    this._tarkistaR13Pilkku2('453', numberMap, errors)
    this._tarkistaR13Pilkku2('454', numberMap, errors)
    this._tarkistaR13Pilkku2('455', numberMap, errors)
    this._tarkistaR13Pilkku2('456', numberMap, errors)
    this._tarkistaR13Pilkku2('457', numberMap, errors)

    this._tarkistaR13Pilkku2('464', numberMap, errors)
    this._tarkistaR13Pilkku2('465', numberMap, errors)
    this._tarkistaR13Pilkku2('458', numberMap, errors)
    this._tarkistaR13Pilkku2('459', numberMap, errors)
    this._tarkistaR13Pilkku2('460', numberMap, errors)
    this._tarkistaR13Pilkku2('461', numberMap, errors)
    this._tarkistaR13Pilkku2('462', numberMap, errors)
    this._tarkistaR13Pilkku2('573', numberMap, errors)
    this._tarkistaR13Pilkku2('574', numberMap, errors)

    this._tarkistaR13Pilkku2('470', numberMap, errors)
    this._tarkistaR13Pilkku2('495', numberMap, errors)

    this._tarkistaR13Pilkku2('496', numberMap, errors)

    this._tarkistaR13Pilkku2('474', numberMap, errors)
    this._tarkistaR13Pilkku2('475', numberMap, errors)
    this._tarkistaR13Pilkku2('476', numberMap, errors)
    this._tarkistaR13Pilkku2('477', numberMap, errors)

    this._tarkistaR13Pilkku2('677', numberMap, errors)
    this._tarkistaR13Pilkku2('678', numberMap, errors)
    this._tarkistaR13Pilkku2('679', numberMap, errors)
    this._tarkistaR13Pilkku2('680', numberMap, errors)

    return errors
  }
}
